import {
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
  ChangeDetectorRef,
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Observable, Subject, take, takeUntil, filter, tap } from 'rxjs';
import { DataService } from 'src/@order/services/data.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthServiceLocal } from 'src/@order/services/auth.service';
import {
  PACKAGESIZETYPE,
  cashSerive,
  deliveryMethods,
  handlingMethods,
  paymentMethods,
  standardPackageSizes,
} from 'src/@order/commonData/commonUiData';
import { animate, style, transition, trigger } from '@angular/animations';
import { OrderPool } from 'src/app/utils/systemEnums';
import { standardOrderModel } from 'src/@order/commonData/standardOrderInitialize.model';
import { PromoCodesDialogComponent } from 'src/@order/dialogs/promo-codes-dialog/promo-codes-dialog.component';
import { formatDate } from '@angular/common';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { InfoDialogComponent } from 'src/@order/dialogs/info-dialog/info-dialog.component';
import { NoDriverAvailableDialogComponent } from 'src/@order/dialogs/no-driver-available-dialog/no-driver-available-dialog.component';
import { appMessages } from 'src/@order/commonData/app-messages';
import { ComponentCanDeactivate } from 'src/app/core/auth/guards/pending-changes.guard';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { AutoSuggestPromoComponent } from 'src/@order/dialogs/auto-suggest-promo/auto-suggest-promo.component';
import { ProgressBarService } from 'src/@order/components/progress-bar/progress-bar.service';
import { ConfirmationDialogComponent } from 'src/@order/dialogs/confirmation-dialog/confirmation-dialog.component';
import { FacebookApiService } from 'src/@order/services/FacebookApi.service';
import { SaverDeliveryMethodInfoComponent } from 'src/@order/components/bottom-sheets/saver-delivery-method-info/saver-delivery-method-info.component';
import * as DeviceDetector from 'device-detector-js';
import { MatDrawer } from '@angular/material/sidenav';

interface DeviceInfo {
  modelName: string;
  brand: string;
  type: string;
  os: string;
}

export class EnhancedDeviceDetector {
  private deviceDetector = new (DeviceDetector as any)();

  getDeviceInfo(): DeviceInfo {
    const device = this.deviceDetector.parse(navigator.userAgent);
    // Get basic info
    const deviceType = device.device?.type || 'desktop';
    const osName = device.os?.name || 'Unknown';
    // Enhanced model detection
    const modelName = this.detectModel(device, navigator.userAgent);
    const brand = this.detectBrand(device, navigator.userAgent);

    return {
      modelName,
      brand,
      type: deviceType,
      os: osName,
    };
  }

  private detectModel(device: any, userAgent: string): string {
    // Check if mobile/tablet first
    if (device.device?.model) {
      return device.device.model;
    }
    // Desktop/laptop detection
    if (device.device?.type === 'desktop' || !device.device?.type) {
      return this.detectDesktopModel(userAgent);
    }
    return device.device?.type || 'Unknown';
  }

  private detectBrand(device: any, userAgent: string): string {
    if (device.device?.brand) {
      return device.device.brand;
    }
    // Detect based on OS and user agent
    const osName = device.os?.name?.toLowerCase() || '';
    if (osName.includes('mac') || userAgent.includes('Macintosh')) {
      return 'Apple';
    }
    if (osName.includes('windows')) {
      return this.detectWindowsBrand(userAgent);
    }
    return 'Unknown';
  }

  private detectDesktopModel(userAgent: string): string {
    // Mac detection
    if (userAgent.includes('Macintosh')) {
      if (userAgent.includes('MacBook')) {
        return 'MacBook';
      } else if (userAgent.includes('iMac')) {
        return 'iMac';
      } else if (userAgent.includes('Mac Pro')) {
        return 'Mac Pro';
      }
      return 'Mac';
    }
    // Windows - unfortunately, specific manufacturer info is rarely available
    if (userAgent.includes('Windows')) {
      // Some edge cases where manufacturer might be detectable
      if (userAgent.includes('Dell')) return 'Dell';
      if (userAgent.includes('HP')) return 'HP';
      if (userAgent.includes('Lenovo')) return 'Lenovo';
      if (userAgent.includes('ASUS')) return 'ASUS';
      if (userAgent.includes('Acer')) return 'Acer';
      return 'Windows PC';
    }
    return 'Desktop';
  }

  private detectWindowsBrand(userAgent: string): string {
    // Limited detection possible from user agent
    const brands = ['Dell', 'HP', 'Lenovo', 'ASUS', 'Acer', 'MSI', 'Samsung'];
    for (const brand of brands) {
      if (userAgent.includes(brand)) {
        return brand;
      }
    }
    return 'Windows PC';
  }
}

@Component({
  selector: 'app-standard-order',
  templateUrl: './heavy-items.component.html',
  styleUrls: ['./heavy-items.component.scss'],
  animations: [
    trigger('showHide', [
      transition(':enter', [
        style({ transform: 'translateY(-20px)', opacity: 0 }),
        animate(200, style({ transform: 'translateY(0px)', opacity: 1 })),
      ]),
      transition(':leave', [
        animate(200, style({ transform: 'translateY(-20px)', opacity: 0 })),
      ]),
    ]),
  ],
})
export class HeavyItemsComponent
  implements OnInit, OnDestroy, ComponentCanDeactivate
{
  /* to check where to user come (whatsapp or direct) */
  redirectByWA: boolean = false;

  // Firebase optimization properties
  private pendingFirebaseUpdates: any = {};
  private firebaseUpdateTimeout: any = null;
  private readonly FIREBASE_DEBOUNCE_DELAY = 3000; // Increased to 3 seconds for better batching
  private lastUpdateSnapshot: string = ''; // Track last update to avoid redundant writes
  private updateCount: number = 0; // Track update frequency
  private readonly MAX_UPDATES_PER_MINUTE = 20; // Limit updates to reduce costs

  tipText: string = '';

  /* for ui text */
  headings = {
    packageDestination: `What's Your package destination?`,
    relocate: `Move or Relocate:`,
    whatToPlan: `What's the Plan?`,
    pickupLocation: `Pickup Location`,
    dropOffLocation: `Dropoff Location`,
  };

  /* to save user/pdas data */
  customerData;

  /* get important data form local*/
  handlingMethods = JSON.parse(JSON.stringify(handlingMethods));
  deliveryMethods = JSON.parse(JSON.stringify(deliveryMethods));
  paymentMethods = JSON.parse(JSON.stringify(paymentMethods));
  cashSerive = JSON.parse(JSON.stringify(cashSerive));
  packageSizes: PACKAGESIZETYPE[] = JSON.parse(
    JSON.stringify(standardPackageSizes)
  );

  /* general variable */
  selectedCountryCode;
  handlingData = [];
  handlingItem;
  currentTime = new Date();
  groupIndex;
  handlingAllData;
  selectedDeliveryMethod;
  selectedPaymentMethod;
  isPromoSuggested: boolean = false;
  promoCodeList: [] = [];
  appliedPromo;
  form!: FormGroup;
  currentStep: number = 1;
  docIdFire;
  private unsubscribe$ = new Subject<void>();
  orderDoc: any = null;
  confirmOrderRes: any;
  filteredDeliveryMethods: any = [];
  selectedHandling;
  selectedPackageSize: any;

  /* general flags */
  isConfirmingOrder: boolean = false;
  isAttachmentUploaded: boolean = false;
  attachmentUploadedUrl: string;
  isDataLoading: boolean = false;
  detectClosingWindow: boolean = false;
  orderConfirmApiCalled: boolean = false;
  isHeavyItemAndRelocateOrder: boolean = false;
  isOrderConfirmed: boolean = false;
  selectedCashService;
  isResetting: boolean = false; // Flag to prevent saving during reset

  isSeverPlan: boolean = false;
  isBusinessPartner: boolean = false;

  // Payment summary related properties
  dropPointsToShow: any[] = [];
  isDeliveryChargeLoading: boolean = false;

  /* FOR CERRY ORDER DATA FROM LOCALST. */
  pendingOrderData;
  hardReload: boolean = false;

  isSaverPlanBottomSheetShow: boolean = false;
  isPharmacyOrder: boolean = false;

  private isRestoringFromLocal = false;

  activeDropoffIndex: number = 0; // Track which dropoff is currently active

  /* detect before page reload */
  @HostListener('window:beforeunload')
  canDeactivate(): any {
    // returning false will show a confirm dialog before navigating away
    if (localStorage.getItem('PWAToken')) {
      if (
        this.form.get('pickupLocationAddress')?.value &&
        !this.isConfirmingOrder &&
        !this.hardReload
      ) {
        // this.saveDataInfoLocal();
        return false;
      } else {
        // this.deleteDocument();
        this.unsubscribeFromDocument();
        return true;
      }
    } else {
      return true;
    }
  }

  deviceInfo: DeviceInfo;

  @ViewChild('pickupDrawer') pickupDrawer: MatDrawer;

  drawerContext: { type: 'pickup' | 'dropoff'; dropoffIndex?: number } = {
    type: 'pickup',
  };

  isPickupDrawerOpen = false;

  // Loading state for orderDoc initialization
  isOrderDocLoading: boolean = true;

  // Computed properties for better performance and cleaner template
  get shouldShowPaymentSummary(): boolean {
    return this.anyDropOffSelected() && this.selectedDeliveryMethod?.dropPoint;
  }

  constructor(
    private _fb: FormBuilder,
    private _matDialog: MatDialog,
    private _dataService: DataService,
    private _matSnackBar: MatSnackBar,
    private _bottomSheet: MatBottomSheet,
    private _auth: AuthServiceLocal,
    private _FacebookApiService: FacebookApiService,
    private renderer: Renderer2,
    private _progressBar: ProgressBarService,
    private _router: Router,
    private _ActivatedRoute: ActivatedRoute,
    private afs: AngularFirestore,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    /* check where to user come */
    if (localStorage.getItem('redirectByWA')) {
      this.redirectByWA = true;
    } else {
      this.redirectByWA = false;
    }

    if (localStorage.getItem('isBusinessPartner') == '1') {
      this.isBusinessPartner = true;
    } else {
      this.isBusinessPartner = false;
    }
    /* set country pin code */
    this.selectedCountryCode = localStorage.getItem('phoneCode');

    /* 
      CHECK IS HEAVY ITEM & RELOCATION ITEM OR NOT
    */
  }

  ngOnInit(): void {
    this._ActivatedRoute.url.pipe(take(1)).subscribe((url) => {
      if (url[0].path == 'standard-heavy') {
        this.isHeavyItemAndRelocateOrder = true;
      } else {
        this.isHeavyItemAndRelocateOrder = false;
      }
    });

    // filter handling method accoding to country.
    this.filterHandlingMethodByCountry();

    // filter Delivery method accoding to country.
    this.filterDeliveryMethodByCountry();

    /* creating form here */
    this.createForm();

    // Only add the initial drop row if there is no saved order data
    const hasPendingOrder =
      localStorage.getItem('PENDINGORDERDATA') ||
      localStorage.getItem('PENDINGHEAVYORDERDATA');
    if (!hasPendingOrder) {
      this.addDropRow();
    }

    // Ensure at least one dropoff row exists
    if (this.dropoffLocations.length === 0) {
      this.addDropRow();
    }

    // FIX: Ensure form controls are properly initialized with default values
    // This prevents issues with mat-slide-toggle not working properly
    if (!this.form.get('isLoadingFeeApplied').value) {
      this.form.get('isLoadingFeeApplied').setValue(false);
    }

    // FIX: Ensure the loading fee toggle is properly synchronized
    // This prevents issues with the toggle not reflecting the correct state
    setTimeout(() => {
      const currentValue = this.form.get('isLoadingFeeApplied').value;
      console.log('Form control value after initialization:', currentValue);
      if (currentValue === null || currentValue === undefined) {
        this.form.get('isLoadingFeeApplied').setValue(false);
        console.log('Fixed isLoadingFeeApplied control value');
      }
    }, 100);

    this._ActivatedRoute.queryParams.pipe(take(1)).subscribe((res: any) => {
      // handleWAkey
      if (res?.wa == 1) {
        if (!localStorage.getItem('redirectByWA')) {
          localStorage.setItem('redirectByWA', '1');
        }
      }
    });

    // set country-specific tip text based on logged-in user
    this.setTipText();

    /* MAIN FUNCTION TO START ORDER FLOW */
    this.getAPIdataAndStartOrder();

    // OPTIMIZATION: Debounced form value changes to reduce Firebase writes
    this.form.valueChanges
      .pipe(
        takeUntil(this.unsubscribe$),
        // Add debounce to reduce frequency of updates
        // Only save if we have meaningful data and not during order confirmation or reset
        filter((res) => {
          return !!(
            (
              this.form?.get('pickupLocationAddress')?.value &&
              !this.isConfirmingOrder &&
              !this.isResetting &&
              res &&
              this.docIdFire
            ) // Only save if Firebase is initialized
          );
        })
      )
      .subscribe((res) => {
        // Handle manual changes to dropoff addresses
        this.handleManualDropoffChanges();

        // OPTIMIZATION: Only save to localStorage, not Firebase for form changes
        this.saveDataInfoLocal();
      });

    console.log(
      'this.dropoffLocations?.controls',
      this.dropoffLocations?.controls
    );
  }

  private setTipText(): void {
    const storedCountry = localStorage.getItem('country');
    const phoneCode = localStorage.getItem('phoneCode');
    const country = storedCountry
      ? storedCountry.trim()
      : phoneCode === '+233'
      ? 'Ghana'
      : 'Nigeria';

    if (country === 'Ghana') {
      this.tipText =
        'Tip: To avoid high prices, type the specific location in your area for example a town, city, or popular landmark not just the general area name.';
    } else {
      this.tipText =
        'Tip: To avoid high prices, type the specific location in your area for example a street, neighborhood, or popular landmark not just the broad area name.';
    }
  }

  getAPIdataAndStartOrder() {
    /* FIRST GET ALL IMPORTANT APIS DATA THEN PROCEED TO FATHER FLOW */
    Promise.all([
      this._progressBar.show(),
      this.getOrderHandlingType(),
      this.getAllPromoCode(),
    ]).then(() => {
      /* for check case ([new order] case  or [unsuccessful order which is saved in our localstorage] case) */
      this.getQueryParams();
      this.locationsFireHandling();
      this._progressBar.hide();
    });
  }

  locationsFireHandling() {
    // set pickup location info form field
    this._dataService.selectedLocation
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((result) => {
        console.log('Pickup location selected:', result);
        this.form.get('pickupLocation').setValue(result);
        this.form.get('pickupLocationAddress').setValue(result.address);

        // Update Firebase if document is already initialized
        if (this.docIdFire) {
          console.log(
            'Firebase already initialized, updating pickup address immediately'
          );
          const params = {
            'selectedAddress.from': [result],
            changeStatus: 1,
          };
          this.updateDocWithParams(params);
        } else {
          console.log(
            'Firebase not initialized yet, pickup address will be updated when Firebase gets initialized'
          );
          this.initializeOrder();
        }

        // Unselect delivery method when pickup location changes
        this.resetSelectedDeliveryMethod();

        // Clear payment summary when pickup location changes
        this.dropPointsToShow = [];

        this.saveDataInfoLocal();
      });

    // set dropoff location's info form field.
    this._dataService.dropoffLocation
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((result) => {
        console.log('result', result);

        // UPDATE IN FORM
        this.dropoffLocations.controls[this.groupIndex]
          ?.get('dropoffPointAddress')
          .setValue(result.address);
        this.dropoffLocations.controls[this.groupIndex]
          ?.get('dropoffPoint')
          .setValue(result);

        // update on firebase
        if (this.isHeavyItemAndRelocateOrder) {
          if (this.form.get('globalPackageSize').value) {
            this.dropoffLocations.controls[this.groupIndex]
              ?.get('dropoffPoint')
              .setValue({
                ...this.dropoffLocations.controls[this.groupIndex]?.get(
                  'dropoffPoint'
                ).value,
                packageSize: String(this.form.get('globalPackageSize').value),
              });
            this.dropoffLocations.controls[this.groupIndex]
              ?.get('packageSize')
              .setValue(String(this.form.get('globalPackageSize').value));
            this.updateDropOff();
          } else {
            this.updateDropOff();
          }
        } else {
          this.updateDropOff();
        }

        // Unselect delivery method when any dropoff location changes
        this.resetSelectedDeliveryMethod();

        // Clear payment summary when dropoff location changes
        this.dropPointsToShow = [];

        this.saveDataInfoLocal();
      });

    // remove tip if applied.
    this.form.get('useTip').valueChanges.subscribe((res) => {
      if (!res) {
        this.form.get('tipAmount').setValue('');
        this.form.get('tipAmount').setValidators(null);
        this.form.get('tipAmount').updateValueAndValidity();
        const params = {
          isTipGiven: 0,
          tipAmount: 0,
          tipId: 0,
          changeStatus: 1,
        };
        this.updateDocWithParams(params);
      } else {
        this.form.get('tipAmount').setValidators(Validators.required);
        this.form.get('tipAmount').updateValueAndValidity();
      }
    });

    this.form.get('isLoadingFeeApplied').valueChanges.subscribe((res) => {
      console.log('Loading fee toggle changed:', res);

      if (res) {
        this._matSnackBar.open(appMessages.loadingFeeInfo, 'OKAY', {
          duration: 5000,
        });
        const params = {
          isLoadingFeeApplied: 1,
          changeStatus: 1,
        };
        this.updateDocWithParams(params);
      } else {
        const params = {
          isLoadingFeeApplied: 0,
          changeStatus: 1,
        };
        this.updateDocWithParams(params);
      }

      // Trigger recalculation of delivery charges when loading fee changes
      if (this.selectedDeliveryMethod) {
        this.recalculateDeliveryChargesWithLoadingFee();
      }

      // Save to localStorage immediately to ensure state persistence
      this.saveDataInfoLocal();
    });

    /* 
      Schedule Delivery
    */
    // check is schedule Delivery update on firebase.
    this.form.get('scheduleDelivery').valueChanges.subscribe((res) => {
      if (res && this.form.get('isScheduleDelivery').value) {
        // update on firebase.
        const params = {
          isSchedule: 1,
          scheduleDate: res.toString(),
          time: this._dataService.dateFormate(res),
          changeStatus: 1,
        };
        console.log('params update on firebase', params);

        this.updateDocWithParams(params);
      }
    });
    // uncheck schedule delivery
    this.form.get('isScheduleDelivery').valueChanges.subscribe((res) => {
      if (!res) {
        const params = {
          isSchedule: 0,
          scheduleDate: '',
          time: this._dataService.dateFormate(this.currentTime),
          changeStatus: 1,
        };

        if (this.form.get('scheduleDelivery').value)
          this.form.get('scheduleDelivery').setValue('');

        this.updateDocWithParams(params);
      } else {
        if (!this.form.get('scheduleDelivery').value)
          this.form
            .get('scheduleDelivery')
            .setValue(new Date(this.currentTime));
      }
    });
  }

  getIsTrustCheckApplied(): boolean {
    return !!this.orderDoc?.deliveryCharges?.[0]
      ?.totalPhlenuTrustCheckCommisionApplied;
  }

  get trustCheckTooltip(): string {
    const dropPoints = this.orderDoc?.deliveryCharges[0]?.dropPoint || [];

    const toTitleCase = (str: string) =>
      str.replace(
        /\w\S*/g,
        (txt) => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase()
      );

    const tooltipLines = dropPoints
      .filter((dp) => dp.isPhlenuTrustCheckApplied)
      .map((dp) => `${toTitleCase(dp.name)}: ${dp.phlenuTrustCheckCommision}`);

    return tooltipLines.join('\n');
  }

  getFailbackOrderData(body) {
    this._progressBar.show();
    this._auth.loadFailbackOrderData(body).subscribe((res: any) => {
      if (res.response == 1) {
        this.docIdFire = res?.data?.fbDocId;
      } else {
        this._matSnackBar.open(res.message, 'OKAY', { duration: 3000 });
      }
      this._progressBar.hide();
    });
  }

  subscribeDoc() {
    // get DeliveryCharges changes.
    this.updateOrderDoc()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((updatedDoc: any) => {
        if (updatedDoc) {
          console.log('updatedDoc', updatedDoc);
          // stop spinner
          if (updatedDoc && updatedDoc['changeStatus'] == 0)
            this.isDataLoading = false;

          this.orderDoc = updatedDoc;

          // Stop loading when orderDoc has isPhlenutrustCheckEnabled key
          if (
            updatedDoc &&
            updatedDoc.hasOwnProperty('isPhlenutrustCheckEnabled')
          ) {
            this.isOrderDocLoading = false;
          }

          if (updatedDoc?.isPharmacyOrder == 1) {
            this.isPharmacyOrder = true;
            if (
              updatedDoc.paymentMethod?.length &&
              updatedDoc?.deliveryCharges?.length
            ) {
              this.paymentMethods = this.paymentMethods.filter((item) =>
                updatedDoc?.paymentMethod.find(
                  (ele) => ele == 4 && item.id == 3
                )
              );
            }
          } else {
            this.isPharmacyOrder = false;
          }

          if (updatedDoc.isSaverPlanOn == 1) this.isSeverPlan = true;
          else this.isSeverPlan = false;

          // update deliveryMethods values
          if (updatedDoc?.deliveryCharges?.length) {
            console.log(
              'updatedDoc?.deliveryCharges',
              updatedDoc?.deliveryCharges
            );

            const seletedMethodsId =
              this.filteredDeliveryMethods.filter((item) => item.isSelected)[0]
                ?.id || null;

            this.filteredDeliveryMethods = this.mergeArrays(
              deliveryMethods,
              updatedDoc?.deliveryCharges
            ).sort((a, b) => a.id - b.id);

            if (this.isSeverPlan) {
              let subIdItemFire = updatedDoc?.deliveryCharges.filter(
                (item) => item.subId == 2
              )[0];

              if (subIdItemFire) {
                const subIdItemLocal = deliveryMethods.filter(
                  (item) => item.id == 7
                )[0];

                if (subIdItemLocal) {
                  subIdItemFire['id'] = subIdItemLocal?.id;
                  subIdItemFire['title'] = subIdItemLocal?.title;
                  subIdItemFire['label'] = subIdItemLocal?.label;
                  subIdItemFire['icon'] = subIdItemLocal?.icon;
                  subIdItemFire['isSelected'] = subIdItemLocal?.isSelected;
                  subIdItemFire['notSelectable'] =
                    subIdItemLocal?.notSelectable;
                  // const margeObj = {...subIdItemFire, ...subIdItemLocal}
                  console.log('margeObj', subIdItemFire);
                  // console.log("this.filteredDeliveryMethods", this.filteredDeliveryMethods);
                }
                if (localStorage.getItem('country') == 'Nigeria') {
                  this.filteredDeliveryMethods.splice(1, 0, subIdItemFire);
                } else {
                  this.filteredDeliveryMethods.splice(2, 0, subIdItemFire);
                }
              }

              if (this.isSeverPlan) {
                const temp = JSON.parse(
                  JSON.stringify(this.filteredDeliveryMethods)
                );
                console.log('temp', temp);
                const rearrangedArr = [
                  // temp.slice(0, 1)[0], // {id: 1}
                  // temp.slice(1, 2)[0],  // {id: 2}
                  // temp.slice(2, 3)[0], // {id: 3}
                ];
                // console.log("rearrangedArr", rearrangedArr);

                // this.filteredDeliveryMethods = rearrangedArr;
              }

              // console.log("filteredDeliveryMethods==--=>", this.filteredDeliveryMethods);
            }

            if (
              JSON.parse(localStorage.getItem('PENDINGORDERDATA'))
                ?.selectedDeliveryMethod?.id == 7
            ) {
              this.filteredDeliveryMethods.forEach((item) =>
                item.id ==
                JSON.parse(localStorage.getItem('PENDINGORDERDATA'))
                  ?.selectedDeliveryMethod?.id
                  ? (item.isSelected = 1)
                  : null
              );
            }

            if (seletedMethodsId) {
              this.filteredDeliveryMethods.map((item) => {
                if (item.id === seletedMethodsId) {
                  item.isSelected = 1;
                  this.selectedDeliveryMethod = item;

                  // Update dropPointsToShow when delivery method is updated from Firebase
                  if (this.selectedDeliveryMethod?.dropPoint) {
                    this.dropPointsToShow =
                      this.selectedDeliveryMethod.dropPoint.map(
                        (dropPoint) => ({
                          ...dropPoint,
                          // Store original charges for loading fee calculations
                          originalCharges: dropPoint.charges,
                          originalPayToCustomer: dropPoint.payToCustomer,
                        })
                      );
                  } else {
                    this.dropPointsToShow = [];
                  }
                }
              });
            }
          }

          // selected loader default
          if (
            updatedDoc?.isLoadingFeeApplied == 1 &&
            !this.form.get('isLoadingFeeApplied').value
          ) {
            this.form.get('isLoadingFeeApplied').setValue(true);
            // Recalculate delivery charges when loading fee is enabled from Firebase
            if (this.selectedDeliveryMethod) {
              this.recalculateDeliveryChargesWithLoadingFee();
            }
          } else if (
            updatedDoc?.isLoadingFeeApplied == 0 &&
            this.form.get('isLoadingFeeApplied').value
          ) {
            this.form.get('isLoadingFeeApplied').setValue(false);
            // Recalculate delivery charges when loading fee is disabled from Firebase
            if (this.selectedDeliveryMethod) {
              this.recalculateDeliveryChargesWithLoadingFee();
            }
          }
        }
      });
  }

  openBottomSheet() {
    this.isPromoSuggested = true;
    if (
      this.promoCodeList?.length &&
      !this.isPharmacyOrder &&
      !this.isSeverPlan
    )
      this._bottomSheet
        .open(AutoSuggestPromoComponent, {
          panelClass: ['bottomSheetWrapper'],
          disableClose: true,
          data: {
            title: 'Use Promo',
            promoList: this.promoCodeList.length ? this.promoCodeList : [],
          },
        })
        .afterDismissed()
        .subscribe((res) => {
          if (res) {
            this.appliedPromo = res;
            this.form.get('isPromoApplied').setValue(true);
            this.form.get('promoTitle').setValue(res.promoTitle);
            const params = {
              isPromoCodeApplied: 1,
              promoTitle: res.promoTitle,
              changeStatus: 1,
              promoStatus: 0,
            };
            this.updateDocWithParams(params);
          } else {
            this.form.get('isPromoApplied').setValue(false);
            this.form.get('promoTitle').setValue('');
            this.removePromoCode();
          }
        });
  }

  goToNextStep(): void {
    // You can add validation here before moving to the next step
    if (
      this.form.get('pickupLocationAddress').valid &&
      this.dropoffLocations.controls[0]?.get('dropoffPointAddress').valid
    ) {
      this.currentStep = 2;
    } else {
      // Mark controls as touched to show validation errors
      this.form.get('pickupLocationAddress').markAsTouched();
      this.dropoffLocations.controls[0]
        ?.get('dropoffPointAddress')
        .markAsTouched();
    }
  }

  // You might want a back button as well for navigation
  goToPreviousStep(): void {
    this.currentStep = 1;
    // Validate and update payment summary when going back to step 1
    this.validateAndUpdatePaymentSummary();
  }

  // Method to validate and update payment summary
  private validateAndUpdatePaymentSummary(): void {
    // Check if we have a selected delivery method but no valid dropoff points
    if (this.selectedDeliveryMethod && this.dropPointsToShow.length === 0) {
      // Reset delivery method selection if no valid dropoff points
      this.resetSelectedDeliveryMethod();
      this.dropPointsToShow = [];
    }

    // Check if we have valid dropoff locations but no delivery method selected
    const hasValidDropoffs = this.anyDropOffSelected();
    if (hasValidDropoffs && !this.selectedDeliveryMethod) {
      // Clear payment summary if no delivery method is selected
      this.dropPointsToShow = [];
    }
  }

  mergeArrays(firstArray, secondArray) {
    const thirdArray = [];
    if (this.isSeverPlan) {
      console.log('merge if else');
      console.log('secondArray', secondArray);
      console.log('firstArray', firstArray);
      secondArray.forEach((obj2) => {
        if (obj2?.subId != 2) {
          const matchedObj = firstArray.find((obj1) => obj1.id === obj2.id);
          if (matchedObj) {
            const mergedObj = { ...matchedObj, ...obj2 };
            console.log('mergedObj', mergedObj);
            thirdArray.push(mergedObj);
          }
        }
      });
    } else {
      console.log('merge else');

      secondArray.forEach((obj2) => {
        const matchedObj = firstArray.find((obj1) => obj1.id === obj2.id);
        if (matchedObj) {
          const mergedObj = { ...matchedObj, ...obj2 };
          thirdArray.push(mergedObj);
        }
      });
    }
    return thirdArray;
  }

  anyDropOffSelected() {
    return (
      this.dropoffLocations?.value?.some(
        (val) => val?.dropoffPointAddress !== ''
      ) || false
    );
  }

  filterHandlingMethodByCountry() {
    if (this.isHeavyItemAndRelocateOrder) {
      this.handlingMethods = this.handlingMethods.filter(
        (methods) => methods.id == 3 || methods.id == 4
      );
    } else {
      if (localStorage.getItem('country') == 'Nigeria') {
        this.handlingMethods = this.handlingMethods.filter(
          (methods) => methods.id == 1 || methods.id == 2
        );
      } else {
        this.handlingMethods = this.handlingMethods.filter(
          (methods) => methods.id == 1 || methods.id == 2
        );
      }
    }
  }

  filterDeliveryMethodByCountry() {
    if (this.isHeavyItemAndRelocateOrder) {
      if (localStorage.getItem('country') == 'Nigeria') {
        this.deliveryMethods = this.deliveryMethods.filter(
          (method) =>
            method.id === 3 ||
            method.id === 4 ||
            method.id === 5 ||
            method.id === 6
        );
        console.log('this.deliveryMethods', this.deliveryMethods);
      } else {
        this.deliveryMethods = this.deliveryMethods.filter(
          (method) => method.id === 6 || method.id === 3 || method.id === 5
        );
      }
    } else {
      if (localStorage.getItem('country') == 'Nigeria') {
        this.deliveryMethods = this.deliveryMethods.filter(
          (method) => method.id === 2 || method.id === 3 || method.id === 4
        );
      } else {
        this.deliveryMethods = this.deliveryMethods.filter(
          (method) => method.id === 1 || method.id === 2 || method.id === 3
        );
      }
    }
  }

  initializeOrder() {
    // Start loading state for new order
    this.isOrderDocLoading = true;

    this.customerData = JSON.parse(localStorage.getItem('authUser'));
    console.log('customerData===>', this.customerData);
    const time = new Date();
    standardOrderModel['PDAS_Id'] = this.customerData.PDAS_Id;
    standardOrderModel['userId'] = this.customerData.id;
    const docId = `${standardOrderModel['userId']}_${time.getTime()}`;
    standardOrderModel['uniqueId'] = docId;
    (standardOrderModel['time'] = time.toISOString()),
      (standardOrderModel['region'] = localStorage.getItem('country'));
    standardOrderModel['createdAt'] = time;
    standardOrderModel['calculateWithCar'] = 1;
    standardOrderModel['isHeavyRelocationOrder'] = this
      .isHeavyItemAndRelocateOrder
      ? 1
      : 0;
    standardOrderModel['calculateWithTruck'] =
      localStorage.getItem('country') == 'Ghana' ? 1 : 1;

    // Use EnhancedDeviceDetector to get device info
    const enhancedDeviceDetector = new EnhancedDeviceDetector();
    this.deviceInfo = enhancedDeviceDetector.getDeviceInfo();
    standardOrderModel['modelName'] = this.deviceInfo.modelName;

    console.log('Creating Firebase document with docId:', docId);
    this.afs
      .collection(OrderPool)
      .doc(docId)
      .set(standardOrderModel)
      .then(() => {
        console.log('Firebase document created successfully');
        this.docIdFire = docId;

        // Subscribe to document changes after creation
        this.subscribeDoc();

        // Update any pending pickup location data
        this.updatePendingPickupLocation();
      })
      .catch((error) => {
        console.error('Error creating Firebase document:', error);
        this._matSnackBar.open(
          'Failed to initialize order. Please try again.',
          'OKAY',
          {
            duration: 3000,
          }
        );
      });
  }

  // Method to update pending pickup location data after Firebase is initialized
  private updatePendingPickupLocation() {
    const pickupLocation = this.form.get('pickupLocation').value;
    const pickupAddress = this.form.get('pickupLocationAddress').value;

    if (pickupLocation && pickupAddress && this.docIdFire) {
      console.log('Updating pending pickup location to Firebase');
      const params = {
        'selectedAddress.from': [pickupLocation],
        changeStatus: 1,
      };
      this.updateDocWithParams(params);
    }
  }

  getAllPromoCode() {
    return new Promise((resolve, reject) => {
      this._auth
        .getPromoCodeList({
          minimiumOrderValue: 0,
          orderType: this.isHeavyItemAndRelocateOrder ? 4 : 1,
        })
        .subscribe((res: any) => {
          if (res.response) {
            this.promoCodeList = res.data.promoCodeList;
            resolve(true);
          } else {
            resolve(true);
            // this._matSnackBar.open(res.message, 'Okay', { duration: 3000 });
            this.promoCodeList = [];
          }
        });
    });
  }

  getOrderHandlingType() {
    return new Promise((resovle, reject) => {
      this._auth.getOrderHandlingType().subscribe((res: any) => {
        if (res.response == 200) {
          this.handlingAllData = res.data;
          resovle(true);
        } else {
          this._matSnackBar.open(res.message, 'Okay', { duration: 3000 });
          reject;
        }
      });
    });
  }

  addNewKey(array) {
    array.forEach((obj) => {
      obj['isSelected'] = 0;
    });
    return array;
  }

  selectHandlingMethods(selectedIndex) {
    if (
      !this.dropoffLocations.controls[
        this.dropoffLocations.controls.length - 1
      ].get('dropoffPointAddress').value
    ) {
      this.showSelectionError('dropoffPointAddress');
    } else if (!this.selectedPackageSize) {
      this.showSelectionError('packageSizeEmpty');
    } else {
      this.selectLogic(this.handlingMethods, selectedIndex);

      this.selectedHandling = this.handlingMethods.filter(
        (item) => item.isSelected
      )[0];

      // "Packaged"
      if (this.selectedHandling?.id == 1) {
        this.handlingData = this.addNewKey(this.handlingAllData.package);
        this.resetSelectedDeliveryMethod();
        this.handlingItem = null;
      }
      // "care"
      else if (this.selectedHandling?.id == 2) {
        this.handlingData = this.addNewKey(this.handlingAllData.care);
        this.resetSelectedDeliveryMethod();
        this.handlingItem = null;
      }
      // "heavy"
      else if (this.selectedHandling?.id == 3) {
        this.handlingData = this.addNewKey(this.handlingAllData.heavy);
        this.resetSelectedDeliveryMethod();
        this.handlingItem = null;
      }
      // "relocation"
      else if (this.selectedHandling?.id == 4) {
        this.handlingData = this.addNewKey(this.handlingAllData.relocation);
        this.resetSelectedDeliveryMethod();
        this.handlingItem = null;
      }

      this.saveDataInfoLocal();
    }
  }

  resetSelectedDeliveryMethod() {
    // Clear selection flags on both arrays and nullify the selected item
    if (this.deliveryMethods && Array.isArray(this.deliveryMethods)) {
      this.deliveryMethods.forEach((item) => (item.isSelected = 0));
    }
    if (
      this.filteredDeliveryMethods &&
      Array.isArray(this.filteredDeliveryMethods)
    ) {
      this.filteredDeliveryMethods.forEach((item) => (item.isSelected = 0));
    }
    this.selectedDeliveryMethod = null;
  }

  onSelectPackagedItem(selectedIndex) {
    if (!this.handlingItem && !this.isPromoSuggested) {
      setTimeout(() => {
        this.openBottomSheet();
      }, 500);
    }
    this.selectLogic(this.handlingData, selectedIndex);
    this.handlingItem = this.handlingData.filter((item) => item.isSelected)[0]; //fsfd

    if (
      this.form.get('attachment').value ||
      this.form.get('attachmentInfo').value
    )
      this.resetAttachment();

    this.saveDataInfoLocal();

    const params = {
      changeStatus: 1,
      sandingItemKey: this.handlingItem.id,
      sandingItemName: this.form.get('attachmentInfo').value
        ? this.form.get('attachmentInfo').value
        : this.handlingItem.title,
    };

    console.log('hello in the params of selected package item', params);

    this.updateDocWithParams(params);
  }

  onSelectPackage(i) {
    // change key based on selected change.
    this.packageSizes.map((item, index) => {
      if (i == index) {
        item.isSelected = 1;
        this.selectedPackageSize = item;
      } else {
        item.isSelected = 0;
      }
    });

    // update in globle form.
    this.form.get('globalPackageSize').setValue(this.selectedPackageSize.id);

    if (this.isHeavyItemAndRelocateOrder) {
      this.dropoffLocations.controls.forEach((group) => {
        if (group.get('dropoffPoint').value) {
          group.get('dropoffPoint').value['packageSize'] = String(
            this.selectedPackageSize.id
          );
          group
            ?.get('packageSize')
            .setValue(String(this.selectedPackageSize.id));
        }
      });
    }

    // update on firebase.
    this.updateDropOff();

    this.saveDataInfoLocal();
  }
  resetAttachment() {
    this.form.get('attachmentInfo').setValue('');
    this.form.get('attachment').setValue('');
    this.isAttachmentUploaded = false;
    this.attachmentUploadedUrl = '';
  }

  onSelectdeliveryMethods(selectedIndex) {
    if (!this.form.get('pickupLocationAddress').value) {
      this.showSelectionError('pickupLocationAddress');
    } else if (
      !this.dropoffLocations.controls[
        this.dropoffLocations.controls.length - 1
      ].get('dropoffPointAddress').value
    ) {
      this.showSelectionError('dropoffPointAddress');
    } else {
      this.selectLogic(this.filteredDeliveryMethods, selectedIndex);
      this.selectedDeliveryMethod = this.filteredDeliveryMethods.filter(
        (item) => item.isSelected
      )[0];

      // Update dropPointsToShow for payment summary
      if (this.selectedDeliveryMethod?.dropPoint) {
        this.dropPointsToShow = this.selectedDeliveryMethod.dropPoint.map(
          (dropPoint) => ({
            ...dropPoint,
            // Store original charges for loading fee calculations
            originalCharges: dropPoint.charges,
            originalPayToCustomer: dropPoint.payToCustomer,
          })
        );
      } else {
        this.dropPointsToShow = [];
      }

      // Initialize loading fee state when delivery method is selected
      if (
        this.form.get('isLoadingFeeApplied').value &&
        this.selectedDeliveryMethod
      ) {
        this.selectedDeliveryMethod.loadingFee =
          this.selectedDeliveryMethod.loadingFee || 5;
        // Apply loading fee to dropPointsToShow if it's already enabled
        this.recalculateDeliveryChargesWithLoadingFee();
      } else if (this.selectedDeliveryMethod) {
        this.selectedDeliveryMethod.loadingFee = 0;
      }

      this.saveDataInfoLocal();

      // NEW: Update delivery charges in each dropoffPoint
      if (
        this.selectedDeliveryMethod &&
        this.selectedDeliveryMethod.dropPoint
      ) {
        this.dropoffLocations.controls.forEach((group, idx) => {
          const dropoffPoint = group.get('dropoffPoint').value;
          const matchedDropPoint = this.selectedDeliveryMethod.dropPoint.find(
            (dp) => dp.address === dropoffPoint.address
          );
          if (matchedDropPoint) {
            group.get('dropoffPoint').setValue({
              ...dropoffPoint,
              deliveryCharge: matchedDropPoint.charges, // adjust property as needed
            });
          }
        });
        this.updateDropOff();
      }

      if (
        this.selectedDeliveryMethod?.id == 7 &&
        !this.isSaverPlanBottomSheetShow
      ) {
        this.openSaverBottomSheet();
        if (this.form.get('isScheduleDelivery').value) {
          this.form.get('isScheduleDelivery').setValue('');
          this.form.get('scheduleDelivery').setValue('');
        }
      }
    }
  }

  openSaverBottomSheet() {
    this._bottomSheet
      .open(SaverDeliveryMethodInfoComponent, {
        panelClass: ['bottomSheetWrapper'],
        disableClose: false,
      })
      .afterDismissed()
      .subscribe(() => {
        this.isSaverPlanBottomSheetShow = true;
        this.saveDataInfoLocal();
      });
  }

  onSelectPaymentMethod(selectedItem, selectedIndex) {
    this.paymentMethods.forEach((item) => (item.isSelected = 0));
    if (!this.form.get('pickupLocationAddress').value) {
      this.showSelectionError('pickupLocationAddress');
      return;
    } else if (
      !this.dropoffLocations.controls[
        this.dropoffLocations.controls.length - 1
      ].get('dropoffPointAddress').value
    ) {
      this.showSelectionError('dropoffPointAddress');
      return;
    } else if (!this.selectedDeliveryMethod) {
      this.showSelectionError('deliveryMethod');
      return;
    } else if (
      this.selectedDeliveryMethod?.pdasWalletAmount == 0 &&
      selectedIndex == 2
    ) {
      this.showSelectionError('noEnoughBalance');
      return;
    } else {
      selectedItem.isSelected = 1;

      this.selectedPaymentMethod = selectedItem;

      // if(this.selectedPaymentMethod?.id !== 1){
      // 	this.selectedCashService = null
      // }
      console.log('this.selectedPaymentMethod', this.selectedPaymentMethod);

      const params = {
        isPdasWalletApplied: this.selectedPaymentMethod.id == 3 ? 1 : 0,
        changeStatus: 1,
      };

      this.updateDocWithParams(params);

      this.saveDataInfoLocal();
    }
  }

  onSelectCashSerive(selectedIndex) {
    this.selectLogic(this.cashSerive, selectedIndex);
    console.log('thiscashSerive===>==>=>', this.cashSerive);
    this.selectedCashService = this.cashSerive.filter(
      (item) => item.isSelected
    )[0];
    console.log('this.selectedCashService', this.selectedCashService);

    this.saveDataInfoLocal();
  }

  selectLogic(arr, selectedIndex) {
    arr.forEach((element, index): any => {
      if (selectedIndex == index) {
        return (element.isSelected = 1);
      } else {
        element.isSelected = 0;
      }
    });
  }

  public isTotalCashCollection() {
    if (this.dropoffLocations.controls.length) {
      return this.dropoffLocations.controls.some(
        (group) => Number(group.get('dropoffPoint').value?.collectCash) > 0
      );
    } else {
      return false;
    }
  }

  getTotalCashCollection() {
    if (this.dropoffLocations.controls.length) {
      let sumOfCashCollected = 0;
      this.orderDoc?.selectedAddress?.to.forEach((item) => {
        if (item.collectCash > 0) {
          sumOfCashCollected += parseFloat(item.collectCash);
        }
      });
      return sumOfCashCollected;
    } else {
      return false;
    }
  }

  /* 
    FORM FUNCTIONS
  */

  // create form here.
  private createForm(data = undefined): void {
    this.form = this._fb.group({
      pickupLocationAddress: ['', Validators.required],
      pickupLocation: ['', Validators.required],
      dropoffLocations: this._fb.array([]),
      isScheduleDelivery: [''],
      scheduleDelivery: [''],
      isPromoApplied: [false],
      useTip: [''],
      tipAmount: [''],
      isPhlenuCashApplied: [false],
      isLoadingFeeApplied: [false],
      promoTitle: [''],
      comment: [''],
      attachmentInfo: [''],
      attachment: [''],
      globalPackageSize: [
        '',
        this.isHeavyItemAndRelocateOrder
          ? Validators.required
          : Validators.nullValidator,
      ],
    });

    console.log('Form created with initial values:', this.form.value);
    console.log(
      'isLoadingFeeApplied initial value:',
      this.form.get('isLoadingFeeApplied').value
    );
  }

  preventInvalidKeys(event: KeyboardEvent) {
    const invalidChars = ['-', '+', 'e', 'E'];
    if (invalidChars.includes(event.key)) {
      event.preventDefault();
    }
  }

  // form array for dropoffLocation
  get dropoffLocations(): FormArray {
    return this.form.get('dropoffLocations') as FormArray;
  }

  // ADD NEW DROP LOCATION FORM GROUP
  public addDropRow(index?, isOpenDrop: boolean = true): void {
    // console.log("index", index);

    (<FormArray>this.form.get('dropoffLocations')).push(
      this._fb.group({
        dropoffPointAddress: ['', Validators.required],
        dropoffPoint: ['', Validators.required],
        packageSize: [
          '',
          this.isHeavyItemAndRelocateOrder ? Validators.required : null,
        ],
      })
    );

    // FIX: Handle dropoff location changes when adding new rows
    // This ensures handling data is reset when new dropoff locations are added
    this.handleDropoffLocationChange();

    if (isOpenDrop)
      if (index || index == 0) {
        this.onDropOffLocation(this.dropoffLocations['controls'], index + 1);
      }
  }

  // REMOVE DROP LOCATION FORM GROUP
  public removeDropLocation(dropIndex): void {
    // remove form firebase
    if (this.dropoffLocations.controls.length == 1) {
      this.dropoffLocations.controls[dropIndex]
        .get('dropoffPointAddress')
        .setValue('');
      this.dropoffLocations.controls[dropIndex]
        .get('dropoffPoint')
        .setValue('');
      this.dropoffLocations.controls[dropIndex].get('packageSize').setValue('');
    } else {
      (<FormArray>this.form.get('dropoffLocations')).removeAt(dropIndex);
    }

    // Handle dropoff location changes
    this.handleDropoffLocationChange();

    this.updateDropOff();
  }

  /* 
    ERROR'S HANDLING
  */

  showSelectionError(errorfor) {
    switch (errorfor) {
      case 'pickupLocationAddress':
        this._matSnackBar.open(appMessages.enterPickupLocation, 'OKAY', {
          duration: 4000,
        });
        break;
      case 'dropoffPointAddress':
        this._matSnackBar.open(appMessages.enterDropOffLocation, 'OKAY', {
          duration: 4000,
        });
        break;
      case 'deliveryMethod':
        this._matSnackBar.open(appMessages.selectDeliveryMethod, 'OKAY', {
          duration: 4000,
        });
        break;
      case 'noEnoughBalance':
        this._matSnackBar.open(appMessages.noEnoughBalance, 'OKAY', {
          duration: 4000,
        });
        break;
      case 'promoNotAvailable':
        this._matSnackBar.open(appMessages.invalidPromoCode, 'OKAY', {
          duration: 4000,
        });
        break;
      case 'promoExpired':
        this._matSnackBar.open(appMessages.expiredPromo, 'OKAY', {
          duration: 4000,
        });
        break;
      case 'handlingItem':
        this._matSnackBar.open(appMessages.selectHandlingItem, 'OKAY', {
          duration: 4000,
        });
        break;
      case 'paymentMethod':
        this._matSnackBar.open(appMessages.selectPaymentmethod, 'OKAY', {
          duration: 4000,
        });
        break;
      case 'showSelectionError':
        this._matSnackBar.open(appMessages.cashOnServiceType, 'OKAY', {
          duration: 4000,
        });
        break;
      case 'packageSizeEmpty':
        this._matSnackBar.open(appMessages.packageSizeEmpty, 'OKAY', {
          duration: 4000,
        });
        break;
      default:
        break;
    }
  }

  onChangeTipAmount() {
    if (this.form.get('tipAmount').value) {
      const isTip = this.form.get('useTip').value;
      const tipAmount = this.form.get('tipAmount').value || 0;
      const params = {
        isTipGiven: !!isTip ? 1 : 0,
        tipAmount: tipAmount,
        tipId: 0,
        changeStatus: 1,
      };
      this.updateDocWithParams(params);
    } else {
      this.form.get('useTip').setValue(false);
    }
  }

  /* 
    FROM FUNCTION FINISHED HERE...
  */

  // OPEN PICKUP LOCATION DIALOG.
  public onPickupLocation(): void {
    this.drawerContext = { type: 'pickup' };
    this.openPickupDrawer();
  }

  // OPEN DROPOFF LOCATION DIALOG.
  public onDropOffLocation(groups, groupIndex: number): void {
    this.activeDropoffIndex = groupIndex; // Set the active index
    this.drawerContext = { type: 'dropoff', dropoffIndex: groupIndex };
    if (!this.form.get('pickupLocationAddress').value) {
      console.log(
        'pickupLocationAddress',
        this.form.get('pickupLocationAddress').value
      );
      this.showSelectionError('pickupLocationAddress');
    } else {
      this.openPickupDrawer();
    }
  }

  public openPromoCodeDialog(): void {
    const dialog = this._matDialog.open(PromoCodesDialogComponent, {
      autoFocus: false,
      width: '610px',
      disableClose: true,
      data: {
        heading: 'Promo code',
        orderType: 'standard',
        promoList: this.promoCodeList.length ? this.promoCodeList : [],
      },
    });

    dialog.afterClosed().subscribe((res) => {
      if (res) {
        this.appliedPromo = res;
        this.form.get('isPromoApplied').setValue(true);
        this.form.get('promoTitle').setValue(res.promoTitle);
        const params = {
          isPromoCodeApplied: 1,
          promoTitle: res.promoTitle,
          changeStatus: 1,
          promoStatus: 0,
        };
        this.updateDocWithParams(params);

        setTimeout(() => {
          this.updateOrderDoc()
            .pipe(take(1))
            .subscribe((updatedDoc: any) => {
              if (
                updatedDoc?.deliveryCharges?.length &&
                updatedDoc?.isPromoCodeApplied &&
                this.selectedDeliveryMethod
              ) {
                const tempSelectedDeliveryChanges =
                  updatedDoc?.deliveryCharges.filter(
                    (item) => item.id == this.selectedDeliveryMethod.id
                  );
                console.log(
                  'tempSelectedDeliveryChanges',
                  tempSelectedDeliveryChanges
                );
                if (!tempSelectedDeliveryChanges?.length) return;
                this.checkPromoValidation(tempSelectedDeliveryChanges[0]);
              }
            });
        }, 1500);
      } else {
        this.appliedPromo = null;
        this.removePromoCode();
      }
    });
  }

  private checkPromoValidation(promoResponse): void {
    console.log('checkPromoValidation CALLED');
    if (!promoResponse) return;
    if (promoResponse?.promoResponse) {
      const promoStatus = promoResponse?.promoResponse.promoStatus;
      if (promoStatus == 2 || promoStatus == 3) {
        this.showSelectionError('promoNotAvailable');
        this.form.get('promoTitle').markAllAsTouched();
        this.form.get('promoTitle').setErrors({ inValid: true });
        // this._cd
      } else if (promoStatus == 4 || promoStatus == 5) {
        this.showSelectionError('promoExpired');
        this.form.get('promoTitle').markAllAsTouched();
        this.form.get('promoTitle').setErrors({ inValid: true });
      }
    }
  }

  public removePromoCode(): void {
    this.form.get('isPromoApplied').setValue(false);
    this.form.get('promoTitle').setValue('');
    const params = {
      isPromoCodeApplied: 0,
      promoTitle: '',
      changeStatus: 1,
      promoStatus: 0,
    };
    this.updateDocWithParams(params);
  }

  public onAttachmentSelect(event): void {
    const reader = new FileReader();
    if (event.target.files && event.target.files.length) {
      const file = event.target.files[0];
      reader.readAsDataURL(file);
      reader.onload = () => {
        this._auth.uploadFile(file).subscribe((res: any) => {
          if (res.response) {
            this._matSnackBar.open(res.message, 'OKAY', { duration: 2000 });
            this.form.get('attachment').markAsPristine();
            this.form.get('attachment').markAsUntouched();
            this.attachmentUploadedUrl = res.data.url;
            this.isAttachmentUploaded = true;
            this.saveDataInfoLocal();
          }
        });
      };
    }
  }

  public onRemoveAttachment(): void {
    this.form.get('attachment').setValue('');
    this.isAttachmentUploaded = false;
    this.attachmentUploadedUrl = '';
  }

  // DROPOFF ADD/UPDATE/REMOVE
  public updateDropOff() {
    let tempDropOff = [];
    this.dropoffLocations.controls.forEach((group) => {
      tempDropOff.push(group.get('dropoffPoint').value);
    });

    // Check if we have valid dropoff locations before proceeding
    const validDropoffs = tempDropOff.filter(
      (dropoff) => dropoff && dropoff.address && dropoff.address.trim() !== ''
    );

    // If no valid dropoffs, reset delivery method and payment summary
    if (validDropoffs.length === 0) {
      console.log(
        'No valid dropoff locations found, resetting delivery method and payment summary'
      );
      this.resetSelectedDeliveryMethod();
      this.dropPointsToShow = [];
      return;
    }

    // CONDITIONAL FIREBASE INITIALIZATION: Create Firebase document if it doesn't exist
    if (!this.docIdFire) {
      console.log(
        'Firebase document not initialized. Creating document now...'
      );
      this.initializeOrder();
      // Wait for Firebase to be ready before updating
      setTimeout(() => {
        this.updateDropOffToFirebase(validDropoffs);
      }, 1000);
    } else {
      // Firebase already exists, update directly
      this.updateDropOffToFirebase(validDropoffs);
    }
  }

  // Separate method to update dropoff data to Firebase
  private updateDropOffToFirebase(dropoffs: any[]) {
    const params = {
      'selectedAddress.to': dropoffs,
      changeStatus: 1,
    };
    this.updateDocWithParams(params);
  }

  private unsubscribeFromDocument() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.unsubscribe$ = new Subject<void>(); // Reinitialize to allow resubscription
  }

  private resubscribeToDocument() {
    // this.unsubscribe$ = new Subject<void>(); // Reinitialize to allow resubscription
    this.subscribeDoc();
  }

  private async deleteDocument() {
    const docSnapshot = await this.afs
      .collection(OrderPool)
      .doc(this.docIdFire)
      .delete();
  }

  /* 
    FIREBASE QUERY'S FUNTIONS
  */

  // OPTIMIZATION: Enhanced Firebase document access with caching
  private cachedOrderDoc: any = null;
  private lastDocReadTime: number = 0;
  private readonly DOC_CACHE_DURATION = 5000; // 5 seconds cache

  // get/update deliveryCharges by firebase.
  updateOrderDoc() {
    if (!this.docIdFire) {
      console.log(
        'Firebase document not initialized yet. Returning empty observable.'
      );
      // Return an empty observable when Firebase is not initialized
      return new Observable();
    }

    // OPTIMIZATION: Return cached data if recent enough
    const now = Date.now();
    if (
      this.cachedOrderDoc &&
      now - this.lastDocReadTime < this.DOC_CACHE_DURATION
    ) {
      console.log('Returning cached Firebase document data');
      return new Observable((observer) => {
        observer.next(this.cachedOrderDoc);
        observer.complete();
      });
    }

    console.log('Accessing Firebase document with docIdFire:', this.docIdFire);
    return this.afs
      .collection(OrderPool)
      .doc(this.docIdFire)
      .valueChanges()
      .pipe(
        // OPTIMIZATION: Cache successful reads
        tap((doc: any) => {
          if (doc) {
            this.cachedOrderDoc = doc;
            this.lastDocReadTime = now;
          }
        })
      );
  }

  updateDocWithParams(params) {
    if (params['changeStatus']) this.isDataLoading = true;

    // Only update Firebase if document is initialized
    if (!this.docIdFire) {
      console.log(
        'Firebase document not initialized yet. Skipping update until drop-off location is selected.'
      );
      return;
    }

    // OPTIMIZATION: Check for duplicate updates to avoid unnecessary writes
    const updateSnapshot = JSON.stringify(params);
    // Do not skip duplicates for critical updates; they must always go through
    const isCritical = this.isCriticalUpdate(params);
    if (!isCritical && updateSnapshot === this.lastUpdateSnapshot) {
      console.log('Skipping duplicate Firebase update');
      return;
    }

    // OPTIMIZATION: Rate limiting to prevent excessive updates
    this.updateCount++;
    if (this.updateCount > this.MAX_UPDATES_PER_MINUTE) {
      console.log('Update rate limit reached, batching update');
      this.batchFirebaseUpdate(params);
      return;
    }

    // Reset update count every minute
    setTimeout(() => {
      this.updateCount = Math.max(0, this.updateCount - 1);
    }, 60000);

    // SAFE OPTIMIZATION: Only batch non-critical updates
    // Critical updates (like location changes) still go immediately
    if (isCritical) {
      // Immediate update for critical operations
      if (this.docIdFire) {
        console.log('CRITICAL UPDATE - Immediate Firebase write:', params);
        const docRef = this.afs.collection(OrderPool).doc(this.docIdFire);
        docRef
          .update(params)
          .then(() => {
            this.lastUpdateSnapshot = updateSnapshot;
            this.isDataLoading = false;
          })
          .catch((error) => {
            console.error('Firebase update failed:', error);
            this.isDataLoading = false;
          });
      }
    } else {
      // Batch non-critical updates
      this.batchFirebaseUpdate(params);
    }
  }

  // Check if this is a critical update that needs immediate processing
  private isCriticalUpdate(params: any): boolean {
    // Critical updates that affect pricing or order flow
    const criticalKeys = [
      'selectedAddress.from',
      'selectedAddress.to',
      'changeStatus',
      'isSchedule',
      'scheduleDate',
      'isPromoCodeApplied',
      'promoTitle',
      'isTipGiven',
      'tipAmount',
      'isLoadingFeeApplied',
    ];

    return Object.keys(params).some((key) => criticalKeys.includes(key));
  }

  // OPTIMIZATION: Enhanced batching method to reduce Firebase costs
  private batchFirebaseUpdate(params: any) {
    // Merge the new params with pending updates efficiently
    Object.keys(params).forEach((key) => {
      if (Array.isArray(params[key])) {
        // For arrays, use the latest value
        this.pendingFirebaseUpdates[key] = params[key];
      } else if (typeof params[key] === 'object' && params[key] !== null) {
        // For objects, merge them
        this.pendingFirebaseUpdates[key] = {
          ...this.pendingFirebaseUpdates[key],
          ...params[key],
        };
      } else {
        // For primitives, use the latest value
        this.pendingFirebaseUpdates[key] = params[key];
      }
    });

    // Clear existing timeout
    if (this.firebaseUpdateTimeout) {
      clearTimeout(this.firebaseUpdateTimeout);
    }

    // OPTIMIZATION: Dynamic debounce delay based on update frequency
    const debounceDelay = this.getDynamicDebounceDelay();

    // Set new timeout for batched update
    this.firebaseUpdateTimeout = setTimeout(() => {
      this.executeFirebaseUpdate();
    }, debounceDelay);
  }

  // OPTIMIZATION: Dynamic debounce delay based on update frequency
  private getDynamicDebounceDelay(): number {
    const pendingUpdateCount = Object.keys(this.pendingFirebaseUpdates).length;

    // Shorter delay for fewer updates, longer for many
    if (pendingUpdateCount <= 2) {
      return 1000; // 1 second for 1-2 updates
    } else if (pendingUpdateCount <= 5) {
      return 2000; // 2 seconds for 3-5 updates
    } else {
      return this.FIREBASE_DEBOUNCE_DELAY; // 3 seconds for 6+ updates
    }
  }

  // OPTIMIZATION: Enhanced Firebase update execution with cost tracking
  private executeFirebaseUpdate() {
    if (this.docIdFire && Object.keys(this.pendingFirebaseUpdates).length > 0) {
      const updateCount = Object.keys(this.pendingFirebaseUpdates).length;
      console.log(
        `BATCHED UPDATE - Processing ${updateCount} fields:`,
        this.pendingFirebaseUpdates
      );

      const docRef = this.afs.collection(OrderPool).doc(this.docIdFire);
      docRef
        .update(this.pendingFirebaseUpdates)
        .then(() => {
          console.log(
            `Firebase batch update successful (${updateCount} fields)`
          );
          this.lastUpdateSnapshot = JSON.stringify(this.pendingFirebaseUpdates);
          this.isDataLoading = false;

          // OPTIMIZATION: Track successful updates for monitoring
          this.logUpdateMetrics(updateCount);
        })
        .catch((error) => {
          console.error('Firebase batch update failed:', error);
          this.isDataLoading = false;

          // OPTIMIZATION: Retry failed batch updates
          this.retryBatchUpdate();
        });

      // Clear pending updates
      this.pendingFirebaseUpdates = {};
      this.firebaseUpdateTimeout = null;
    }
  }

  // OPTIMIZATION: Log update metrics for cost monitoring
  private logUpdateMetrics(fieldCount: number) {
    const timestamp = new Date().toISOString();
    console.log(
      `[${timestamp}] Firebase update: ${fieldCount} fields processed`
    );

    // You can extend this to send metrics to your analytics service
    // this.analyticsService.trackFirebaseUpdate(fieldCount);
  }

  // OPTIMIZATION: Retry logic for failed batch updates
  private retryBatchUpdate(retryCount: number = 0) {
    const maxRetries = 2;

    if (
      retryCount < maxRetries &&
      Object.keys(this.pendingFirebaseUpdates).length > 0
    ) {
      console.log(
        `Retrying batch update (attempt ${retryCount + 1}/${maxRetries})`
      );

      setTimeout(() => {
        this.executeFirebaseUpdate();
      }, 2000 * (retryCount + 1)); // Exponential backoff
    } else if (retryCount >= maxRetries) {
      console.error('Max retries reached for batch update');
      this._matSnackBar.open(
        'Failed to save some changes. Please try again.',
        'OKAY',
        {
          duration: 3000,
        }
      );
    }
  }

  checkZeroNumber(value) {
    if (String(value).split('')[0] == '0') {
      return true;
    } else {
      return false;
    }
  }

  phoneNumberTrim(field) {
    return this.checkZeroNumber(field)
      ? this.selectedCountryCode == '+233'
        ? String(field).split('').slice(1, 10).join('')
        : String(field).split('').slice(1, 11).join('')
      : field;
  }

  onPrint() {
    console.log('form', this.form);
  }

  onConfirm() {
    if (this.isHeavyItemAndRelocateOrder) {
      this.dropoffLocations.controls.map((group) => {
        group
          .get('packageSize')
          .setValue(this.form.get('globalPackageSize').value);
      });
    }

    console.log('the upadated orderDoc', this.orderDoc);

    console.log('form', this.form);
    console.log('this.isPharmacyOrder', this.isPharmacyOrder);

    console.log(
      'this.orderDoc?.paymentMethod?.length',
      this.orderDoc?.paymentMethod?.length
    );
    console.log('this.selectedPaymentMethod', !this.selectedPaymentMethod);

    // handle isPharmacyOrder payment methods
    // if(!this.selectedPaymentMethod && this.isPharmacyOrder) this.onSelectPaymentMethod(1); this.selectedCashService
    if (this.isPharmacyOrder) {
      if (this.orderDoc?.paymentMethod?.length) {
        if (!this.selectedPaymentMethod) {
          this.showSelectionError('paymentMethod');
          this.isConfirmingOrder = false;
          return;
        }
      } else {
        this.onSelectPaymentMethod(
          this.paymentMethods.filter((item) => item.id == 1)[0],
          0
        );
        this.onSelectCashSerive(0);
      }
    } else {
      if (this.selectedPaymentMethod?.id == 1 && !this.selectedCashService) {
        this.showSelectionError('showSelectionError');
        this.isConfirmingOrder = false;
        return;
      }
    }

    if (
      this.form.invalid ||
      !this.handlingItem ||
      !this.selectedDeliveryMethod ||
      !this.selectedPaymentMethod ||
      !this.selectedPackageSize
    ) {
      if (!this.form.get('pickupLocationAddress').value) {
        this.showSelectionError('pickupLocationAddress');
        this.form.get('pickupLocationAddress').markAsTouched();
      } else if (
        !this.dropoffLocations.controls[
          this.dropoffLocations.controls.length - 1
        ].get('dropoffPointAddress').value
      ) {
        this.showSelectionError('dropoffPointAddress');
      } else if (!this.selectedPackageSize) {
        this.showSelectionError('packageSizeEmpty');
      } else if (!this.handlingItem) {
        this.showSelectionError('handlingItem');
      } else if (!this.selectedDeliveryMethod) {
        this.showSelectionError('deliveryMethod');
      } else if (!this.selectedPaymentMethod && !this.isPharmacyOrder) {
        this.showSelectionError('paymentMethod');
      } else if (
        this.isHeavyItemAndRelocateOrder &&
        !this.form.get('globalPackageSize').value
      ) {
        this.showSelectionError('packageSizeEmpty');
      }
    } else {
      console.log('we are in else');

      this.unsubscribeFromDocument();

      this.saveDataInfoLocal();

      this.isConfirmingOrder = true;

      const modifiedpickupLocationFormData = JSON.parse(
        JSON.stringify(this.form.get('pickupLocation').value)
      );
      modifiedpickupLocationFormData['mobile'] = this.phoneNumberTrim(
        this.form.get('pickupLocation')?.value?.mobile
      );
      modifiedpickupLocationFormData['phone'] =
        modifiedpickupLocationFormData['mobile'];

      this.dropoffLocations.controls.map((group) => {
        group.get('dropoffPoint').value['mobile'] = this.phoneNumberTrim(
          group.get('dropoffPoint').value.mobile
        );
        group.get('dropoffPoint').value['phone'] =
          group.get('dropoffPoint').value['mobile'];
      });

      const payload = {
        region: this.orderDoc.region,
        vehicleType:
          this.selectedDeliveryMethod.id == 7
            ? 2
            : this.selectedDeliveryMethod.id,
        pickupAddresses: !!this.form.get('pickupLocationAddress').value
          ? {
              ...modifiedpickupLocationFormData,
              PDAS_Id: this.orderDoc.PDAS_Id,
              isDefault: 1,
            }
          : null,
        dropAddresses: this.dropoffLocations.controls.map((group) => ({
          ...group.get('dropoffPoint').value,
          PDAS_Id: this.orderDoc.PDAS_Id,
          isDefault: 1,
          heavyAttachment: this.attachmentUploadedUrl || '',
        })),
        // paymentMethod: this.selectedPaymentMethod.id == 4 ? 3 : this.selectedPaymentMethod.id,
        // paymentMethod: this.selectedPaymentMethod.id == 2 ? 3 : this.selectedPaymentMethod.id,
        // paymentMethod: this.selectedPaymentMethod.id == 1 ? this.selectedCashService.id : this.selectedPaymentMethod.id == 2 ? 3 : this.selectedPaymentMethod.id == 3 ? 4 : 0,
        // paymentMethod: this.isPharmacyOrder == false ? this.selectedPaymentMethod.id == 4 ? 3 : this.selectedPaymentMethod.id == 1 ? this.selectedCashService.id : this.selectedPaymentMethod.id  == 2 ? 3 : this.selectedPaymentMethod.id : this.orderDoc?.paymentMethod?.length ? 4 : 2,
        paymentMethod:
          this.isPharmacyOrder == false
            ? this.selectedPaymentMethod.id == 4
              ? 3
              : this.selectedPaymentMethod.id == 1
              ? this.selectedCashService.id
              : this.selectedPaymentMethod.id == 2
              ? 3
              : this.selectedPaymentMethod.id
            : this.orderDoc?.paymentMethod?.length
            ? 3
            : 2,

        isPromoCodeApplied:
          this.orderDoc.isPromoCodeApplied &&
          this.selectedDeliveryMethod?.promoResponse?.promoStatus == 1
            ? 1
            : 0,
        promoId: this.appliedPromo?.id || 0,
        promoAmount:
          this.selectedDeliveryMethod?.promoResponse?.promoDiscountAmount || 0,
        promoDiscountValue:
          this.selectedDeliveryMethod?.promoResponse?.promoDiscountValue || 0,
        promoDiscountType:
          this.selectedDeliveryMethod?.promoResponse?.discountType || 0,

        tipAmount: this.orderDoc.tipAmount,
        isTipGiven: this.orderDoc.isTipGiven,

        discount: this.selectedDeliveryMethod.discountinpercentage,
        imageURL: this.attachmentUploadedUrl || '',
        comment: this.form.get('comment').value,

        isHeavyRelocationOrder: this.isHeavyItemAndRelocateOrder ? 1 : 0,

        sandingItemKey: this.handlingItem.id,
        sandingItemName: this.form.get('attachmentInfo').value
          ? this.form.get('attachmentInfo').value
          : this.handlingItem.title,
        sandingItemType: this.handlingData.filter((item) => item.isSelected)[0]
          .id,
        distance: this.selectedDeliveryMethod?.distance || '',
        isSchedule: this.orderDoc.isSchedule || '',

        isWalletUsed: this.form.get('isPhlenuCashApplied').value ? 1 : 0,
        walletAmount: this.selectedDeliveryMethod?.phlenuCashAmount || 0,

        pdasWalletAmount:
          this.selectedDeliveryMethod?.pdasWalletUsedAmount || 0,
        isPdasWalletUsed: this.orderDoc?.isPdasWalletApplied ? 1 : 0,

        scheduleDate: this.orderDoc.scheduleDate
          ? new Date(this.orderDoc.scheduleDate).toISOString()
          : new Date().toISOString(),
        time: this.orderDoc.isSchedule
          ? formatDate(
              new Date(this.orderDoc.scheduleDate),
              'yyyy-MM-dd hh:mm a',
              'en-US'
            )
          : formatDate(new Date(), 'yyyy-MM-dd hh:mm a', 'en-US'),

        pdasCommisionPercentage:
          this.selectedDeliveryMethod?.pdasCommisionPercentage,
        isPdasCommisionApplied:
          this.selectedDeliveryMethod?.isPdasCommisionApplied,
        pdasCommisionAmount: this.selectedDeliveryMethod?.cashBack,
        conveniencePercentage: this.orderDoc.conveniencePercentage,
        isConvenienceApply: this.orderDoc.isConvenienceApply,
        convenienceFee: this.selectedDeliveryMethod?.convenienceFee,
        percentageOnCashCollect:
          this.selectedDeliveryMethod?.percentageOnCashCollect || 0,
        feeOnCashCollect: this.selectedDeliveryMethod?.feeOnCashCollect || 0,

        damageCoverPercentage: this.orderDoc.damageCoverPercentage || 0,
        damageCoverAmount: this.selectedDeliveryMethod?.damageCoverAmount || 0,
        totalCashCollected: this.isTotalCashCollection()
          ? this.getTotalCashCollection()
          : 0,
        isLoadingFeeApplied: this.form.get('isLoadingFeeApplied').value ? 1 : 0, // applicable in ng case.
        loadingFee: this.selectedDeliveryMethod?.loadingFee,
        isHeavyAttachmentApplied: this.handlingMethods.filter(
          (item) => item.id == 3 && item.isSelected
        ).length
          ? 1
          : 0,

        calculateWithCar: this.selectedDeliveryMethod?.id === 3 ? 1 : 0,
        calculateWithTruck:
          this.selectedDeliveryMethod?.id === 5 ||
          this.selectedDeliveryMethod?.id === 6
            ? 1
            : 0,
        isWebOrder: 1,

        orderPackageSize: this.form.get('globalPackageSize')?.value
          ? this.form.get('globalPackageSize')?.value
          : 0,

        fbDocId: this.docIdFire,
        isWhatsappOrder: this.redirectByWA ? 1 : 0,
        isSaverPlanOn:
          (this.isSeverPlan && this.selectedDeliveryMethod.id) == 7 ? 1 : 0,
        isPharmacyOrder: this.isPharmacyOrder ? 1 : 0,
        userId: this.orderDoc?.userId ? this.orderDoc?.userId : null,

        perishablesIncreasePercent: this.orderDoc?.perishablesIncreasePercent,
        motorBikePlatformPriceIncrease:
          this.orderDoc?.motorBikePlatformPriceIncrease,
        carPlatformPriceIncrease: this.orderDoc?.carPlatformPriceIncrease,
        conveniencePercentageOffline:
          this.orderDoc?.conveniencePercentageOffline,
        isOfflinePdas: this.orderDoc?.isOfflinePdas,

        modelName: this.deviceInfo?.modelName,
      };

      if (this.orderDoc?.pharmacyAdditionalkm)
        payload['pharmacyAdditionalkm'] = this.orderDoc?.pharmacyAdditionalkm;

      if (this.orderDoc?.pharmacyAdditionalkmCharge)
        payload['pharmacyAdditionalkmCharge'] =
          this.orderDoc?.pharmacyAdditionalkmCharge;

      if (this.orderDoc?.pharmacyFirstKm)
        payload['pharmacyFirstKm'] = this.orderDoc?.pharmacyFirstKm;

      if (this.orderDoc?.pharmacyPriceForOtherDay)
        payload['pharmacyPriceForOtherDay'] =
          this.orderDoc?.pharmacyPriceForOtherDay;

      if (this.orderDoc?.pharmacyPriceForsunday)
        payload['pharmacyPriceForsunday'] =
          this.orderDoc?.pharmacyPriceForsunday;

      if (this.orderDoc?.pharmacySpecificLocationCharge)
        payload['pharmacySpecificLocationCharge'] =
          this.orderDoc?.pharmacySpecificLocationCharge;

      if (!this.isHeavyItemAndRelocateOrder) {
        payload.dropAddresses = payload.dropAddresses.map(
          ({ packageSize, ...rest }) => ({ ...rest })
        );
        delete payload.orderPackageSize;
      }

      console.log('finalpayloadpaymentMethod', payload);

      this._auth.confirmStandardOrder(payload).subscribe((res: any) => {
        console.log('RES', res);
        if (res?.response == 1) {
          if (res?.data?.paymentUrl && res?.data?.isPaid) {
            this._matSnackBar.open('Processing.', 'OKAY', { duration: 5000 });
          } else {
            // this._matSnackBar.open(res.message, 'OKAY', { duration: 5000 });
          }
          this.confirmOrderRes = res;
          this.checkCaseAndNavigation(res);
        } else if (res?.response == 2) {
          // this._matSnackBar.open(res.message, 'OKAY', {duration : 5000});
          this.isOrderConfirmed = true;
          if (this.isHeavyItemAndRelocateOrder) {
            if (localStorage.getItem('PENDINGHEAVYORDERDATA')) {
              this.addGoogleTags();
              this.addFbEvent('standardheavy', res?.data);
              localStorage.removeItem('PENDINGHEAVYORDERDATA');
            }
          } else {
            if (localStorage.getItem('PENDINGORDERDATA')) {
              this.addGoogleTags();
              this.addFbEvent('standard', res?.data);
              // this._router.navigate(['/order-successful/'+ res?.data?.order_id])
              localStorage.removeItem('PENDINGORDERDATA');
            }
          }

          this.confirmOrderRes = res;
          this.checkCaseAndNavigation(res);
        }
        // if delivery window close
        else if (res?.response == 3) {
          this._matSnackBar.open(res.message, 'OKAY', { duration: 5000 });
          this.form.enable();
          this.isConfirmingOrder = false;
          this.resubscribeToDocument();
        } else if (res.response === 0) {
          this.driverUnavailableAlert();
          this.resubscribeToDocument();
        } else {
          this.form.enable();
          this.isConfirmingOrder = false;
          this.resubscribeToDocument();
        }
      });
    }
  }

  addFbEvent(type, data) {
    const orderData = {
      selectedDeliveryMethod: this.selectedDeliveryMethod,
      orderDoc: this.orderDoc,
      resData: data,
    };
    this._FacebookApiService.sendPurchaseEvent(type, false, orderData);
  }

  addGoogleTags() {
    // get user data
    const user = JSON.parse(localStorage.getItem('authUser'));
    // Add the event snippet for Purchase Conversion
    const item = this.selectedDeliveryMethod?.dropPoint?.length
      ? this.selectedDeliveryMethod.dropPoint.map((item, i) => {
          return {
            item_id: item.id,
            item_name: item.name,
            affiliation: 'Phlenu',
            coupon: this.orderDoc.promoTitle,
            discount: Math.round(item.charges - item.payToCustomer),
            index: i,
            item_brand: 'Phlenu',
            item_category: this.selectedHandling?.title || '',
            price: item.payToCustomer,
            quantity: i + 1,
          };
        })
      : [];
    // Adding data to the GTM Data Layer
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'purchase',
      name: user.firstName,
      email: user.email,
      phone: user.phoneCode + '' + user.phone,
      ecommerce: {
        transaction_id: this.orderDoc.uniqueId,
        value: this.selectedDeliveryMethod?.netTotal,
        tax: '',
        shipping: '',
        currency: this.selectedDeliveryMethod?.currency,
        price: this.selectedDeliveryMethod?.netTotal,
        coupon: this.orderDoc.promoTitle,
        items: JSON.parse(JSON.stringify(item)),
      },
    });
  }

  driverUnavailableAlert() {
    const dialog = this._matDialog.open(NoDriverAvailableDialogComponent, {
      panelClass: ['commonDialogWrapper'],
      backdropClass: 'dialogBackdrop',
      autoFocus: false,
      width: '480px',
      disableClose: false,
    });

    dialog.afterClosed().subscribe((res) => {
      if (res) {
        this.form.enable();
        this.isConfirmingOrder = false;
      }
    });
  }

  allReset() {
    // Set flag to prevent saving during reset and navigation
    this.isResetting = true;

    // this.form.enable();
    // Prevent valueChanges emissions during reset
    this.form.reset({}, { emitEvent: false });
    this.selectedDeliveryMethod = null;
    this.selectedPaymentMethod = null;
    this.orderDoc = null;
    this.handlingMethods.map((item) => (item.isSelected = 0));
    this.deliveryMethods.map((item) => (item.isSelected = 0));
    this.paymentMethods.map((item) => (item.isSelected = 0));
    this.cashSerive.map((item) => (item.isSelected = 0));
    this.isConfirmingOrder = false;

    // Clear pending heavy order data from localStorage
    localStorage.removeItem('PENDINGHEAVYORDERDATA');

    console.log('PENDINGHEAVYORDERDATA is removed');

    // Keep isResetting true until next navigation completes, with a fallback timeout
    const navSub = this._router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        take(1)
      )
      .subscribe(() => {
        this.isResetting = false;
        navSub.unsubscribe();
      });
    setTimeout(() => {
      this.isResetting = false;
      if (navSub && (navSub as any).unsubscribe) {
        (navSub as any).unsubscribe();
      }
    }, 1000);

    // const user = JSON.parse(localStorage.getItem('authUser'))
    this._auth.getDashboardData({});
  }

  goBack() {
    // Check if there's pending data that needs confirmation
    if (
      this.form.get('pickupLocationAddress')?.value &&
      !this.isConfirmingOrder &&
      !this.hardReload
    ) {
      // Show confirmation dialog
      const confirmed = confirm(
        'WARNING: If you leave this page, your form details will be lost and you will need to place your order again.'
      );

      if (confirmed) {
        // User confirmed - clear localStorage and navigate
        this.allReset();
        this._router.navigate(['/']);
      }
      // If user cancels, do nothing (stay on current page)
    } else {
      // No pending data, navigate directly
      this.allReset();
      this._router.navigate(['/']);
    }
  }

  onPhlenuCashToggle(event: any) {
    const walletAmount =
      this.selectedDeliveryMethod?.walletAmt ||
      this.selectedDeliveryMethod?.phlenuCashAmount ||
      0;
    if (!walletAmount) {
      setTimeout(() => {
        this.form.get('isPhlenuCashApplied').setValue(false);
        this.form.get('isPhlenuCashApplied').updateValueAndValidity();
      }, 0);
      this._matSnackBar.open(appMessages.plenuCaseUnavailable, 'OKAY', {
        duration: 4000,
      });
    } else if (walletAmount < 1) {
      setTimeout(() => {
        this.form.get('isPhlenuCashApplied').setValue(false);
        this.form.get('isPhlenuCashApplied').updateValueAndValidity();
      }, 0);
      this._matSnackBar.open(appMessages.noEnoughPhlenuCase, 'OKAY', {
        duration: 4000,
      });
    } else {
      const params = {
        isPhlenuCashApplied: event.checked && walletAmount ? 1 : 0,
        changeStatus: 1,
      };
      this.updateDocWithParams(params);
    }
  }

  /* 
    after order confirm handle success
  */

  checkCaseAndNavigation(response) {
    // console.log("response", response);
    if (response.data.paymentUrl && response.data.isPaid) {
      this.paymentDialog(response.data);
    } else {
      const dialog = this._matDialog.open(InfoDialogComponent, {
        panelClass: ['commonDialogWrapper'],
        backdropClass: 'dialogBackdrop',
        autoFocus: false,
        width: '510px',
        disableClose: false,
        data: {
          heading: 'Order!',
          message: response.message,
          type: 'order',
        },
      });
      dialog.afterClosed().subscribe(() => {
        this.allReset();
        if (this.redirectByWA) {
          window.open(
            `https://wa.me/${
              localStorage.getItem('country') == 'Ghana'
                ? '+233530460178'
                : '+2349166329008'
            }`,
            '_self'
          );
        } else {
          console.log('response', response);
          this._router.navigate([
            'orders/heavydetail/' + response.data.order_id,
          ]);
        }
      });
    }
  }

  paymentDialog(data) {
    window.open(data.paymentUrl, '_self');
    this.allReset();
  }

  /* 
    HANDLE ORDER WITH LOCAL.
  */

  saveDataInfoLocal() {
    if (this.isOrderConfirmed || this.isResetting) return;

    if (this.form.get('pickupLocationAddress')?.value) {
      const pendingOrderData = {
        formData: this.form.value,
        docIdFire: JSON.parse(JSON.stringify(this.docIdFire)),
        selectedHandling: this.selectedHandling || null,
        handlingItem: this.handlingItem || null,
        attachmentUploadedUrl: this.attachmentUploadedUrl || null,
        isAttachmentUploaded: this.isAttachmentUploaded || false,
        selectedDeliveryMethod: this.selectedDeliveryMethod || null,
        selectedPaymentMethod: this.selectedPaymentMethod || null,
        cashSerive: this.selectedCashService ? this.selectedCashService : null,
        isHeavyItemAndRelocateOrder: this.isHeavyItemAndRelocateOrder,
        selectedPackageSize: this.selectedPackageSize,
        isSaverPlanBottomSheetShow: this.isSaverPlanBottomSheetShow,
      };
      if (this.isHeavyItemAndRelocateOrder) {
        localStorage.setItem(
          'PENDINGHEAVYORDERDATA',
          JSON.stringify(pendingOrderData)
        );
      } else {
        localStorage.setItem(
          'PENDINGORDERDATA',
          JSON.stringify(pendingOrderData)
        );
      }
    }
  }

  getQueryParams() {
    // failback case if payment failed or declined
    if (this.isHeavyItemAndRelocateOrder) {
      if (localStorage.getItem('PENDINGHEAVYORDERDATA')) {
        this.pendingOrderData = JSON.parse(
          localStorage.getItem('PENDINGHEAVYORDERDATA')
        );
        this.fillOrderDataByLocalData();
      }
      // Remove Firebase initialization for new orders - only initialize when drop-off location is selected
    } else {
      if (localStorage.getItem('PENDINGORDERDATA')) {
        this.pendingOrderData = JSON.parse(
          localStorage.getItem('PENDINGORDERDATA')
        );
        this.fillOrderDataByLocalData();
      }
      // Remove Firebase initialization for new orders - only initialize when drop-off location is selected
    }
  }

  fillOrderDataByLocalData() {
    this.isRestoringFromLocal = true;
    this.form.disable();
    this.isDataLoading = true;
    this.docIdFire = this.pendingOrderData?.docIdFire;

    // Restore dropoffLocations FormArray first
    const savedDropoffs =
      this.pendingOrderData?.formData?.dropoffLocations || [];
    this.restoreDropoffLocations(savedDropoffs);

    // Now reset the rest of the form (excluding dropoffLocations)
    const { dropoffLocations, ...restFormData } =
      this.pendingOrderData?.formData || {};

    // Ensure boolean values are properly converted for form controls
    if (restFormData.isLoadingFeeApplied !== undefined) {
      restFormData.isLoadingFeeApplied = Boolean(
        restFormData.isLoadingFeeApplied
      );
      console.log(
        'Restoring isLoadingFeeApplied:',
        restFormData.isLoadingFeeApplied
      );
    }
    if (restFormData.isScheduleDelivery !== undefined) {
      restFormData.isScheduleDelivery = Boolean(
        restFormData.isScheduleDelivery
      );
    }
    if (restFormData.isPromoApplied !== undefined) {
      restFormData.isPromoApplied = Boolean(restFormData.isPromoApplied);
    }
    if (restFormData.useTip !== undefined) {
      restFormData.useTip = Boolean(restFormData.useTip);
    }
    if (restFormData.isPhlenuCashApplied !== undefined) {
      restFormData.isPhlenuCashApplied = Boolean(
        restFormData.isPhlenuCashApplied
      );
    }

    // Handle numeric values that might be stored as strings
    if (restFormData.tipAmount !== undefined && restFormData.tipAmount !== '') {
      restFormData.tipAmount = Number(restFormData.tipAmount);
    }

    console.log('Form data before patchValue:', restFormData);
    this.form.patchValue(restFormData);
    console.log('Form values after patchValue:', this.form.value);

    this.reSubscribeOrder();
    this.form.markAllAsTouched();

    // Restore other state as before...
    if (this.pendingOrderData?.selectedHandling) {
      this.setHandlingItemSelected(this.pendingOrderData.selectedHandling);
    }
    if (this.pendingOrderData?.handlingItem) {
      this.setPopularSelected(this.pendingOrderData.handlingItem);
    }
    if (this.pendingOrderData?.formData?.attachmentInfo)
      this.form
        .get('attachmentInfo')
        .setValue(this.pendingOrderData?.formData?.attachmentInfo);

    if (
      this.pendingOrderData?.attachmentUploadedUrl &&
      this.pendingOrderData?.isAttachmentUploaded
    ) {
      this.isAttachmentUploaded = true;
      this.attachmentUploadedUrl = this.pendingOrderData?.attachmentUploadedUrl;
    }
    if (
      this.pendingOrderData?.selectedPackageSize &&
      this.pendingOrderData?.isHeavyItemAndRelocateOrder
    ) {
      this.packageSizes.map((item) => {
        if (item.id == this.pendingOrderData?.selectedPackageSize.id)
          item.isSelected = 1;
      });
      this.selectedPackageSize = this.pendingOrderData?.selectedPackageSize;
    }

    // FIX: Ensure delivery methods are filtered and selected
    this.filterDeliveryMethodByCountry();
    this.filteredDeliveryMethods = [...this.deliveryMethods];
    this.selectedDeliveryMethod =
      this.pendingOrderData?.selectedDeliveryMethod || null;

    // FIX: Restore the selection state on filteredDeliveryMethods
    if (this.selectedDeliveryMethod) {
      this.filteredDeliveryMethods.forEach((item) => {
        if (item.id === this.selectedDeliveryMethod.id) {
          item.isSelected = 1;
        } else {
          item.isSelected = 0;
        }
      });
    }

    // FIX: Populate dropPointsToShow from the restored delivery method
    if (this.selectedDeliveryMethod?.dropPoint) {
      this.dropPointsToShow = this.selectedDeliveryMethod.dropPoint.map(
        (dropPoint) => ({
          ...dropPoint,
          // Store original charges for loading fee calculations
          originalCharges: dropPoint.charges,
          originalPayToCustomer: dropPoint.payToCustomer,
        })
      );
    } else {
      this.dropPointsToShow = [];
    }

    // FIX: Synchronize loading fee state with the form control
    if (
      this.form.get('isLoadingFeeApplied').value &&
      this.selectedDeliveryMethod
    ) {
      // If loading fee is enabled in the form, ensure it's reflected in the delivery method
      this.selectedDeliveryMethod.loadingFee =
        this.selectedDeliveryMethod.loadingFee || 5;
      // Apply loading fee to dropPointsToShow if it's already enabled
      this.recalculateDeliveryChargesWithLoadingFee();
    } else if (this.selectedDeliveryMethod) {
      // If loading fee is disabled, set it to 0
      this.selectedDeliveryMethod.loadingFee = 0;
    }

    // FIX: Ensure form control is properly synchronized after restoration
    setTimeout(() => {
      const currentValue = this.form.get('isLoadingFeeApplied').value;
      console.log('Form control value after restoration:', currentValue);
      if (currentValue === null || currentValue === undefined) {
        this.form.get('isLoadingFeeApplied').setValue(false);
        console.log(
          'Fixed isLoadingFeeApplied control value after restoration'
        );
      }
    }, 100);

    this.form.enable();

    // FIX: Store the selected delivery method ID before subscribing to Firebase updates
    const selectedDeliveryMethodId = this.selectedDeliveryMethod?.id;

    this.subscribeDoc();

    // FIX: After subscribing to Firebase updates, restore the selection state if needed
    if (selectedDeliveryMethodId) {
      // Use a timeout to ensure the subscribeDoc has processed the initial data
      setTimeout(() => {
        this.restoreDeliveryMethodSelection(selectedDeliveryMethodId);
      }, 100);
    }
  }

  reSubscribeOrder() {
    this.updateOrderDoc()
      .pipe(take(1))
      .subscribe((updatedDoc: any) => {
        if (updatedDoc) {
          this.orderDoc = updatedDoc;

          // Stop loading when orderDoc has isPhlenutrustCheckEnabled key
          if (updatedDoc.hasOwnProperty('isPhlenutrustCheckEnabled')) {
            this.isOrderDocLoading = false;
          }
          // ...other code...
          // set dropoff address
          if (
            updatedDoc?.selectedAddress?.to?.length &&
            !this.isRestoringFromLocal
          )
            this.setDropAddress(updatedDoc.selectedAddress.to);
          // ...other code...
        }
        this.isDataLoading = false;
        this.isRestoringFromLocal = false; // reset after restore
      });
  }

  gettingErrorResetTheOrder() {
    if (this.isHeavyItemAndRelocateOrder) {
      if (localStorage.getItem('PENDINGHEAVYORDERDATA')) {
        localStorage.removeItem('PENDINGHEAVYORDERDATA');
        window.location.reload();
      }
    } else {
      if (localStorage.getItem('PENDINGORDERDATA')) {
        localStorage.removeItem('PENDINGORDERDATA');
        window.location.reload();
      }
    }
  }

  // Method to handle Firebase initialization when needed
  private ensureFirebaseInitialized(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.docIdFire) {
        resolve();
        return;
      }

      // Check if we have valid dropoff locations to trigger Firebase initialization
      const hasValidDropoffs = this.dropoffLocations?.controls?.some(
        (group) => {
          const dropoff = group.get('dropoffPoint').value;
          return dropoff && dropoff.address && dropoff.address.trim() !== '';
        }
      );

      if (hasValidDropoffs) {
        console.log('Valid dropoff locations found, initializing Firebase...');
        this.initializeOrder();

        // Wait for Firebase to be ready
        const checkInterval = setInterval(() => {
          if (this.docIdFire) {
            clearInterval(checkInterval);
            resolve();
          }
        }, 100);

        // Timeout after 10 seconds
        setTimeout(() => {
          clearInterval(checkInterval);
          reject(new Error('Firebase initialization timeout'));
        }, 10000);
      } else {
        reject(new Error('No valid dropoff locations to initialize Firebase'));
      }
    });
  }

  // set pickup address in fallback case.
  setPickupAddress(result) {
    this.form.get('pickupLocation').setValue(result);
    this.form.get('pickupLocationAddress').setValue(result.address);
  }

  // FIX: Method to restore delivery method selection after Firebase updates
  private restoreDeliveryMethodSelection(selectedId: number): void {
    if (
      this.filteredDeliveryMethods &&
      this.filteredDeliveryMethods.length > 0
    ) {
      this.filteredDeliveryMethods.forEach((item) => {
        if (item.id === selectedId) {
          item.isSelected = 1;
          this.selectedDeliveryMethod = item;
        } else {
          item.isSelected = 0;
        }
      });

      // Update dropPointsToShow for payment summary
      if (this.selectedDeliveryMethod?.dropPoint) {
        this.dropPointsToShow = this.selectedDeliveryMethod.dropPoint.map(
          (dropPoint) => ({
            ...dropPoint,
            // Store original charges for loading fee calculations
            originalCharges: dropPoint.charges,
            originalPayToCustomer: dropPoint.payToCustomer,
          })
        );
      } else {
        this.dropPointsToShow = [];
      }
    }
  }

  // set drop address in fallbase case.
  setDropAddress(dropAddresses: Array<string>[]) {
    // UPDATE IN FORM
    dropAddresses.forEach((result: any, index) => {
      if (index) {
        this.addDropRow(index, false);
        this.dropoffLocations.controls[index]
          ?.get('dropoffPointAddress')
          .setValue(result.address);
        this.dropoffLocations.controls[index]
          ?.get('dropoffPoint')
          .setValue(result);
      } else {
        this.dropoffLocations.controls[index]
          ?.get('dropoffPointAddress')
          .setValue(result.address);
        this.dropoffLocations.controls[index]
          ?.get('dropoffPoint')
          .setValue(result);
      }
    });

    // FIX: Handle dropoff location changes when addresses are updated from Firebase
    // This ensures handling data is reset when dropoff locations change
    this.handleDropoffLocationChange();
  }

  // set handling item as selected in order fallback case.
  setHandlingItemSelected(selectedItem) {
    // set selected checked
    this.handlingMethods.map((item) => {
      if (item.id === selectedItem.id) {
        item.isSelected = 1;
      } else {
        // item.isSelected = 0
      }
    });

    this.selectedHandling = selectedItem;

    // "Packaged"
    if (this.selectedHandling?.id == 1) {
      this.handlingData = this.addNewKey(this.handlingAllData.package);
      this.resetSelectedDeliveryMethod();
      this.handlingItem = null;
    }
    // "care"
    else if (this.selectedHandling?.id == 2) {
      this.handlingData = this.addNewKey(this.handlingAllData.care);
      this.resetSelectedDeliveryMethod();
      this.handlingItem = null;
    }
    // "heavy"
    else if (this.selectedHandling?.id == 3) {
      this.handlingData = this.addNewKey(this.handlingAllData.heavy);
      this.resetSelectedDeliveryMethod();
      this.handlingItem = null;
    }
    // "relocation"
    else if (this.selectedHandling?.id == 4) {
      this.handlingData = this.addNewKey(this.handlingAllData.relocation);
      this.resetSelectedDeliveryMethod();
      this.handlingItem = null;
    }

    // FIX: Re-filter delivery methods and reset selection
    this.filterDeliveryMethodByCountry();
    this.resetSelectedDeliveryMethod();
    this.filteredDeliveryMethods = [...this.deliveryMethods];
    this.selectedDeliveryMethod = null;
  }

  // selection of popular send item in order fallback case.
  setPopularSelected(selectedItem) {
    this.handlingData.map((item) => {
      if (item.id === selectedItem.id) {
        item.isSelected = 1;
      }
    });
    this.handlingItem = selectedItem;
    if (
      this.form.get('attachment').value ||
      this.form.get('attachmentInfo').value
    )
      this.resetAttachment();

    // FIX: Re-filter delivery methods and reset selection
    this.filterDeliveryMethodByCountry();
    this.resetSelectedDeliveryMethod();
    this.filteredDeliveryMethods = [...this.deliveryMethods];
    this.selectedDeliveryMethod = null;
  }

  /* 
    ON COMPONENT DESTROY.
  */
  ngOnDestroy(): void {
    console.log('ngOnDestroy');
    // Don't save data if we're in the middle of resetting
    if (!this.isResetting) {
      this.saveDataInfoLocal();
    }

    // Execute any pending Firebase updates before destroying
    if (this.firebaseUpdateTimeout) {
      clearTimeout(this.firebaseUpdateTimeout);
      this.executeFirebaseUpdate();
    }

    // OPTIMIZATION: Clear cache to free memory
    this.cachedOrderDoc = null;
    this.lastDocReadTime = 0;

    this.unsubscribeFromDocument();

    if (!this.form.get('pickupLocation').value) {
      this.afs
        .collection(OrderPool)
        .doc(this.docIdFire)
        .valueChanges()
        .subscribe()
        .unsubscribe();
      this.deleteDocument();
    }
    this.packageSizes = null;
    this.selectedPackageSize = null;
    this.selectedPackageSize = null;

    this.headings = null;

    this.handlingMethods = [];
    this.deliveryMethods = [];
    this.paymentMethods = [];
    this.cashSerive = [];

    this.handlingData = [];
    this.handlingItem = null;

    this.currentTime = null;

    this.form.reset();
    this.groupIndex = null;
    this.handlingAllData = null;

    this.selectedDeliveryMethod = null;
    this.selectedPaymentMethod = null;

    this.promoCodeList = [];
    this.appliedPromo = [];
    // deliveryCharges:[] = [];
    // selectedDeliveryChanges;
    this.orderDoc = null;
    this.isConfirmingOrder = false;
    this.isAttachmentUploaded = false;
    this.attachmentUploadedUrl = '';
    this.isDataLoading = false;
    this.detectClosingWindow = false;
    this.confirmOrderRes;
    this.orderConfirmApiCalled = false;
    this.isHeavyItemAndRelocateOrder = false;
    this.filteredDeliveryMethods = [];
    this.selectedHandling = null;
    this.docIdFire = null;
    console.error = function () {};
  }

  resetOrderFlow() {
    this.hardReload = true;
    const dialog = this._matDialog.open(ConfirmationDialogComponent, {
      panelClass: ['commonDialogWrapper'],
      backdropClass: 'dialogBackdrop',
      width: '480px',
      data: {
        heading: 'Alert!',
        headingColor: 'text-red',
        message:
          'Proceeding with this action will reset your current order data, and this action cannot be undone. Are you sure you want to proceed?',
        action: {
          ok: 'Continue',
          cancel: 'Cancel',
          align: 'left',
        },
      },
    });

    dialog.afterClosed().subscribe((res) => {
      if (res) {
        if (this.isHeavyItemAndRelocateOrder) {
          if (localStorage.getItem('PENDINGHEAVYORDERDATA')) {
            localStorage.removeItem('PENDINGHEAVYORDERDATA');
            window.location.replace(window.location.href);
          }
        } else {
          if (localStorage.getItem('PENDINGORDERDATA')) {
            localStorage.removeItem('PENDINGORDERDATA');
            window.location.replace(window.location.href);
          }
        }
      } else {
        this.hardReload = false;
      }
    });
  }

  // Helper to restore dropoffLocations FormArray
  private restoreDropoffLocations(savedDropoffs: any[]) {
    const dropoffArray = this.form.get('dropoffLocations') as FormArray;
    // Clear existing controls
    while (dropoffArray.length !== 0) {
      dropoffArray.removeAt(0);
    }
    // Add controls for each saved dropoff
    savedDropoffs.forEach((drop) => {
      dropoffArray.push(
        this._fb.group({
          dropoffPointAddress: [drop.dropoffPointAddress, Validators.required],
          dropoffPoint: [drop.dropoffPoint, Validators.required],
          packageSize: [
            drop.packageSize,
            this.isHeavyItemAndRelocateOrder ? Validators.required : null,
          ],
        })
      );
    });
  }

  public openPickupDrawer(): void {
    this.isPickupDrawerOpen = true;
    this.pickupDrawer.open();
  }

  // Handle the location selected event from the drawer component
  public onPickupLocationSelected(location: any): void {
    console.log('Location selected:', location, 'Context:', this.drawerContext);

    if (this.drawerContext.type === 'pickup') {
      this.form.get('pickupLocation').setValue(location);
      this.form.get('pickupLocationAddress').setValue(location.address);
    } else if (this.drawerContext.type === 'dropoff') {
      const idx = this.drawerContext.dropoffIndex;
      this.dropoffLocations.controls[idx]
        ?.get('dropoffPointAddress')
        .setValue(location.address);
      this.dropoffLocations.controls[idx]
        ?.get('dropoffPoint')
        .setValue(location);

      // Mark the form as touched to trigger validation
      this.dropoffLocations.controls[idx]?.markAsTouched();

      // Handle dropoff location changes
      this.handleDropoffLocationChange();

      // Update Firebase when dropoff location is selected
      this.updateDropOff();
    }
    this.activeDropoffIndex = -1; // Reset active index after selection
    this.pickupDrawer.close();
  }

  public onPickupDrawerClose(): void {
    this.isPickupDrawerOpen = false;
    this.activeDropoffIndex = -1; // Reset active index when drawer closes
    this.pickupDrawer.close();
  }

  // Method to get all dropoff locations for the dialog
  public getAllDropoffLocations(): any[] {
    const allDropoffs = [];
    this.dropoffLocations.controls.forEach((control) => {
      const dropoffPoint = control.get('dropoffPoint').value;
      if (dropoffPoint && dropoffPoint.locationId) {
        allDropoffs.push({
          dropoffPoint: dropoffPoint,
          dropoffPointAddress: control.get('dropoffPointAddress').value,
        });
      }
    });
    return allDropoffs;
  }

  // Helper methods to check if amounts are zero for shimmer display
  isAmountZero(amount: number | undefined | null): boolean {
    return amount === 0 || amount === null || amount === undefined;
  }

  shouldShowShimmer(amount: number | undefined | null): boolean {
    return this.isAmountZero(amount) || this.isDeliveryChargeLoading;
  }

  shouldShowPaymentSummaryShimmer(): boolean {
    // Only show shimmer if we're loading OR if we have a delivery method but no dropoff points
    return (
      this.isDeliveryChargeLoading ||
      this.isDataLoading ||
      (this.selectedDeliveryMethod && !this.dropPointsToShow.length)
    );
  }

  getAmountDisplay(amount: number | undefined | null): string {
    return this.isAmountZero(amount) ? '0' : (amount || 0).toString();
  }

  // Method to handle manual changes to dropoff addresses
  private handleManualDropoffChanges(): void {
    // Check if any dropoff address has been manually changed
    const hasManualChanges = this.dropoffLocations.controls.some((control) => {
      const address = control.get('dropoffPointAddress').value;
      const dropoffPoint = control.get('dropoffPoint').value;

      // If address is filled but dropoffPoint is empty or doesn't match, it's a manual change
      return address && (!dropoffPoint || dropoffPoint.address !== address);
    });

    if (hasManualChanges) {
      // Reset delivery method selection when dropoff addresses are manually changed
      this.resetSelectedDeliveryMethod();
      this.dropPointsToShow = [];

      // FIX: Also reset handling data when dropoff addresses are manually changed
      // This ensures consistency with handleDropoffLocationChange
      this.handlingData = [];
      this.handlingItem = null;
      this.selectedHandling = null;

      // Reset handling methods selection
      if (this.handlingMethods && Array.isArray(this.handlingMethods)) {
        this.handlingMethods.forEach((item) => (item.isSelected = 0));
      }
    }
  }

  // Method to handle dropoff location changes (add, remove, or modify)
  private handleDropoffLocationChange(): void {
    // Reset delivery method selection when dropoff locations change
    this.resetSelectedDeliveryMethod();

    // Clear payment summary
    this.dropPointsToShow = [];

    // FIX: Reset handling data and selected handling item when dropoff locations change
    // This ensures the "Select Package Item" section is properly reset
    this.handlingData = [];
    this.handlingItem = null;
    this.selectedHandling = null;

    // Reset handling methods selection
    if (this.handlingMethods && Array.isArray(this.handlingMethods)) {
      this.handlingMethods.forEach((item) => (item.isSelected = 0));
    }
  }

  // Method to recalculate delivery charges when loading fee changes
  private recalculateDeliveryChargesWithLoadingFee(): void {
    if (
      !this.selectedDeliveryMethod ||
      !this.selectedDeliveryMethod.dropPoint
    ) {
      return;
    }

    // Update the loading fee in the selected delivery method
    const isLoaderApplied = this.form.get('isLoadingFeeApplied').value;
    const loadingFeeAmount = isLoaderApplied
      ? this.selectedDeliveryMethod.loadingFee || 5
      : 0;

    // Update the loading fee amount based on the toggle state
    this.selectedDeliveryMethod.loadingFee = loadingFeeAmount;

    // Update the dropPointsToShow to reflect the new charges
    if (this.dropPointsToShow && this.dropPointsToShow.length > 0) {
      this.dropPointsToShow = this.dropPointsToShow.map((dropPoint) => {
        // Get the original charges without loading fee
        const originalCharges = dropPoint.originalCharges || dropPoint.charges;
        const originalPayToCustomer =
          dropPoint.originalPayToCustomer || dropPoint.payToCustomer;

        // Calculate new charges with loading fee
        const newCharges = originalCharges + loadingFeeAmount;
        const newPayToCustomer = originalPayToCustomer + loadingFeeAmount;

        return {
          ...dropPoint,
          // Store original values for future calculations
          originalCharges: originalCharges,
          originalPayToCustomer: originalPayToCustomer,
          // Update the values that the template uses
          charges: newCharges,
          payToCustomer: newPayToCustomer,
          // Also update totalAmount for consistency
          totalAmount: newCharges,
        };
      });
    }

    // Also update the original dropPoint data in selectedDeliveryMethod
    if (
      this.selectedDeliveryMethod.dropPoint &&
      this.selectedDeliveryMethod.dropPoint.length > 0
    ) {
      this.selectedDeliveryMethod.dropPoint =
        this.selectedDeliveryMethod.dropPoint.map((dropPoint) => {
          const originalCharges =
            dropPoint.originalCharges || dropPoint.charges;
          const originalPayToCustomer =
            dropPoint.originalPayToCustomer || dropPoint.payToCustomer;

          const newCharges = originalCharges + loadingFeeAmount;
          const newPayToCustomer = originalPayToCustomer + loadingFeeAmount;

          return {
            ...dropPoint,
            originalCharges: originalCharges,
            originalPayToCustomer: originalPayToCustomer,
            charges: newCharges,
            payToCustomer: newPayToCustomer,
            totalAmount: newCharges,
          };
        });
    }

    // Save the updated data to localStorage
    this.saveDataInfoLocal();

    // Force change detection to update the UI
    this.changeDetectorRef.detectChanges();
  }
}
